// src/enums.ts
var e = (...values) => values;
var RESERVATION_STATUSES = e("PENDING", "CONFIRMED", "WAITLIST", "IN_HOUSE", "CHECKED_OUT", "CANCELLED", "NO_SHOW");
var INVENTORY_STATUSES = ["PENDING", "CONFIRMED", "IN_HOUSE"];
var GUARANTEE_STATUSES = e("NONE", "DEPOSIT_PENDING", "GUARANTEED");
var HK_STATUSES = e("CLEAN", "DIRTY", "INSPECTED", "PICKUP");
var OCCUPANCY_STATUSES = e("VACANT", "OCCUPIED");
var ROOM_BLOCK_TYPES = e("OOO", "OOS");
var ROOM_CATEGORIES = e("ROOM", "SUITE");
var FOLIO_ACCOUNT_TYPES = e("GUEST", "HOUSE", "PM", "AR", "OUTLET", "EVENT");
var FOLIO_STATUSES = e("OPEN", "CLOSED");
var TXN_KINDS = e("CHARGE", "TAX", "PAYMENT", "ALLOWANCE", "TRANSFER");
var LEDGERS = e("DEPOSIT", "GUEST", "AR", "HOUSE");
var TXN_CATEGORIES = e("ROOM", "FNB", "BANQUET", "LAUNDRY", "SPA", "TRANSPORT", "OTHER", "TAX", "PAYMENT");
var TAXABLE_CATEGORIES = e("ROOM", "FNB", "BANQUET", "LAUNDRY", "SPA", "TRANSPORT", "OTHER");
var PAYMENT_METHODS = e("POS", "TRANSFER", "MONNIFY", "PAYSTACK", "FLW", "CL", "OTA_VCC");
var ONLINE_GATEWAYS = e("MONNIFY", "PAYSTACK", "FLW");
var VERIFICATION_STATUSES = e("NOT_REQUIRED", "PENDING", "VERIFIED", "REJECTED");
var CHANNELS = e("DIRECT", "WEB", "OTA");
var MEAL_PLANS = e("RO", "BB", "HB", "FB");
var DERIVE_TYPES = e("PERCENT", "AMOUNT", "PER_ADULT_AMOUNT");
var PACKAGE_UNITS = e("ADULT_NIGHT", "ROOM_NIGHT", "STAY");
var COMPANY_TYPES = e("CORPORATE", "TRAVEL_AGENT", "GOVERNMENT", "NGO", "OTA");
var GENDERS = e("MALE", "FEMALE", "UNSPECIFIED");
var GL_ACCOUNT_TYPES = e("ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE");
var JOURNAL_SOURCES = e("NIGHT_AUDIT", "MANUAL", "AR", "AP", "BANK", "SETTLEMENT", "OPENING", "CLOSING", "REVERSAL");
var JOURNAL_STATUSES = e("DRAFT", "POSTED", "REVERSED");
var PERIOD_STATUSES = e("OPEN", "CLOSED");
var NIGHT_AUDIT_STATUSES = e("RUNNING", "COMPLETED", "FAILED");

// src/permissions.ts
var PERMISSIONS = {
  "dashboard.view": "View operational dashboard",
  "reservations.view": "View reservations",
  "reservations.create": "Create reservations",
  "reservations.edit": "Modify reservations",
  "reservations.cancel": "Cancel reservations",
  "reservations.reinstate": "Reinstate cancelled / no-show reservations",
  "reservations.assign_room": "Assign and unassign rooms",
  "reservations.override_rate": "Override the calculated room rate",
  "reservations.overbook": "Book beyond available inventory",
  "frontdesk.checkin": "Check guests in",
  "frontdesk.checkout": "Check guests out",
  "frontdesk.room_move": "Move in-house guests to another room",
  "frontdesk.key_issue": "Encode and issue room keys",
  "frontdesk.emergency_list": "Keep the offline emergency list (in-house, arrivals, balances) on this computer",
  "frontdesk.waive_fees": "Waive the early check-in, late check-out or lost key card charge, with a reason",
  "frontdesk.messages": "Take messages and parcels left at the desk for guests, and hand them over",
  "frontdesk.safe_box": "Give guests a safe-deposit box, log each opening and release it",
  "frontdesk.upgrade": "Sell a room upgrade at check-in at the manager\u2019s price",
  "frontdesk.prices": "Set the room upgrade and lost key card prices (the GM, as every price: owner, 2026-10-31)",
  "frontdesk.boxes": "Set up the safe-deposit boxes: add one, change it, take it out of use",
  "shift.close": "Close my cashier shift: my takings by method and card terminal against the terminal slips",
  "shift.review": "Review cashiers' shift closes and their differences",
  // The General Cashier's morning check of the tills (owner, 2026-10-28): read only; signing off stays with the reviewers.
  "shift.view": "See every till shift close and the day's money reconciliation, read only",
  "shift.slips": "Record that a till shift's printed terminal slips reached Finance",
  "service.view": "View guest requests, complaints and wake-up calls hotel-wide (the duty logbook: my own department's)",
  "service.log": "Log guest requests, complaints and wake-up calls",
  // The duty logbook is the shift handover of those who run a shift (owner, 2026-10-18: "supervisors only").
  "service.logbook": "Read and write my department's duty logbook (heads, supervisors, the front desk, security on duty)",
  "service.work": "Work on requests for my department",
  "service.manage": "Assign, verify, close and cancel any request; record complaint compensation",
  // Each department reads its own duty logbook; the GM reads them all (owner, 2026-10-18).
  "service.logbooks": "Read every department's duty logbook (the GM)",
  "guests.view": "View guest profiles",
  "guests.edit": "Create and edit guest profiles",
  "guests.view_id_documents": "View full guest identity document numbers",
  "guests.merge": "Merge duplicate guest profiles",
  "guests.blacklist": "Flag guests as blacklisted",
  "companies.view": "View company / travel agent profiles",
  "companies.edit": "Create and edit company / travel agent profiles",
  "folios.view": "View folios",
  "folios.post_charge": "Post charges to folios",
  "folios.post_payment": "Post payments and deposits",
  "folios.reverse": "Reverse same-day postings",
  "folios.adjust": "Post allowances / adjustments to prior-day charges",
  "folios.transfer": "Transfer postings between folios",
  "folios.route": "Set routing instructions",
  "folios.refund": "Refund guest credit balances",
  "folios.refund_other": "Refund another way when the guest's card or account no longer works (the GM, with a reason)",
  "folios.let_go_owing": "Let a guest leave before the money dropped: the bill moves to Departed Guests Owing, never shown as paid (the GM, with a reason)",
  "folios.close": "Settle and close folios",
  "payments.verify_transfers": "Verify or reject bank transfer payments",
  "rates.view": "View rate plans and rate calendar",
  "rates.manage": "Manage rate plans, prices and restrictions",
  "rates.comp.approve": "Approve complimentary rates",
  "rates.house.approve": "Approve house-use rates",
  "fx.manage": "Set daily exchange rates",
  "rooms.view": "View rooms and room types",
  "rooms.manage": "Configure rooms and room types",
  "rooms.ooo": "Place rooms out of order / out of service",
  "housekeeping.view": "View housekeeping board",
  "housekeeping.update_status": "Update room cleaning status",
  "housekeeping.assign": "Assign rooms to attendants",
  "housekeeping.inspect": "Inspect rooms",
  "housekeeping.post_minibar": "Post minibar consumption to rooms",
  "housekeeping.rush": "Ask housekeeping to rush a room for a waiting guest",
  "housekeeping.team": "Put room attendants in their groups, keep their phone numbers and give them a one-time password (their logins are opened by the administrator, their names are HR's)",
  "housekeeping.setup": "Set the room set-up: the complimentary refills, toiletries and equipment in every room, and their stock items",
  "lostfound.manage": "Record found items, contact the owner and return items to them",
  "lostfound.log": "Log an item found in a room (housekeeping tablet); the register stays with the housekeeping office",
  "lostfound.dispose": "Donate, destroy or hand over unclaimed lost & found items once their keeping period is over",
  // The valuables held are an internal-audit check (owner, 2026-10-19).
  "lostfound.view": "See the lost & found register and the valuables held, changing nothing (the Auditor)",
  "engineering.view": "View work orders",
  "maintenance.view": "The head of maintenance's view: the department's board, contracts and certificates, maintenance figures",
  "engineering.create": "Raise work orders",
  "engineering.work": "Update work orders assigned to me",
  "engineering.manage": "Assign, prioritise and close work orders",
  "engineering.approve": "Approve the kitchen's equipment work orders before maintenance starts (the GM; urgent ones go straight)",
  "pos.use": "Use outlet POS (open checks, order, settle)",
  "pos.void_item": "Void items on checks",
  "pos.discount": "Apply discounts, within the role's limit (fnb.discount_limits)",
  // Free bills (owner, 2026-10-20): asked for at the till with the reason, approved by the GM.
  "pos.comp": "Approve a free bill (complimentary or house use) asked for at the till (the GM)",
  // Never on one's own bill (owner, 2026-11-02): the GM's own bill's free bill goes to the Chairman, from his desk.
  "pos.comp_gm": "Approve or refuse a free bill on the GM's own bill, from the Management desk (the Chairman)",
  "pos.manage_menu": "Keep the menu (dishes, outlets, stations, sold out) and propose a new dish or a price (the F&B Manager)",
  // Menu prices (owner, 2026-10-20): proposed by the F&B Manager, approved by the GM, like room rates.
  "pos.price_approve": "Approve a menu price or a new dish (the GM)",
  "pos.kitchen": "Kitchen display",
  "pos.after_hours": "Open a check outside an outlet's hours or while its area is closed, and sell to non-guests outside public hours (gym, pool)",
  "pos.room_charge_over_limit": "Approve a room charge for a guest who owes more than the hotel's limit",
  "fnb.view": "See the F&B start page: today's sales and covers, room service, breakfast, functions and sold-out dishes",
  "fnb.bookings": "Take, seat and cancel table bookings at the restaurants and the bar",
  "fnb.breakfast": "Check guests in at the breakfast buffet (the main restaurant's team; owner, 2026-10-25)",
  // The rooftop restaurant and bar are F&B's since 2026-10-20; the pool and the gym are the GM's.
  "fnb.outlets": "Close or reopen the rooftop restaurant and bar (weather, an event) (the F&B Manager)",
  "kitchen.view": "See the kitchen office: the day's work and guests' dietary needs, recipes and plate costs, food-safety logs and figures",
  "kitchen.log": "Record fridge, freezer and hot-holding temperatures, the opening and closing checks and the staff meals served",
  "kitchen.recipes": "Write the standard recipes and their allergens (the Executive Chef)",
  // Accounts' view of the kitchen (owner, 2026-10-19): the costs, none of the kitchen's daily work.
  "kitchen.cost": "See the recipes' plate costs, the food cost and the staff meals (Accounts: cost control only)",
  "leisure.view": "See the rooftop page: open and closed areas, who is at the pool and gym, bookings, checks and figures",
  "leisure.desk": "Run the gym desk: admit and sign out, sell memberships, the daily checks, close the gym",
  "leisure.pool": "Run the pool from the rooftop till: sell pool time, admit and sign out, test the water, the pool's daily checks, close the pool (the bartender on duty; owner 2026-10-27)",
  "leisure.manage": "Reopen a closed area, admit over capacity or out of hours, cancel memberships (rooftop supervisor)",
  "inventory.view": "View stores, stock levels and stock movements",
  "inventory.request": "Request stock from the stores (requisitions)",
  // The departments ask the stores; the stores ask Purchasing (owner, 2026-11-03): only the Storekeeper, for what the
  // store is short of, and IT, for its own equipment, raise a purchase request.
  "purchase.raise": "Raise a purchase request: the Storekeeper for what the store is short of, IT for its own equipment",
  "inventory.request_approve": "See your department's requisitions as its head (heads of department; the GM any department's)",
  "inventory.issue": "Receive, issue and transfer stock (storekeeper)",
  "inventory.purchase": "Raise purchase orders",
  "inventory.approve": "Approve purchase orders, waste, stock corrections and counts (within the approval limits)",
  // Requisitions (owner, 2026-10-19, as the management agreed): the department asks, the Chief Accountant reviews it
  // against the department's past use, the GM approves, and only then does the Storekeeper issue it.
  "inventory.review": "Review a department's requisition against its past use: pass it to the GM or send it back (the Chief Accountant)",
  "inventory.issue_approve": "Approve a reviewed requisition so the stores may issue it (the GM; the Chairman for the GM's own)",
  "purchase.check": "Check a purchase request before money is committed: it is needed (stock in store, usage) and its price against the quotations (the Auditor)",
  "purchase.approve": "Approve a purchase request the Auditor and the Chief Accountant passed (the GM within his limit; the Chairman above it)",
  "inventory.count": "Lead stock counts (the Auditor, with the store custodian present; owner 2026-10-13)",
  "inventory.witness": "Witness waste above the limit before it is disposed of, and re-check deliveries against their receipts (the Auditor; owner 2026-10-26)",
  "inventory.waste": "Record waste (spoiled, broken or expired stock) for a second person to approve",
  "inventory.manage": "Manage stores, items and par levels",
  "assets.view": "View the asset register",
  "assets.manage": "Manage the asset register: each asset's details, meters and disposals (the Chief Engineer)",
  // The figures that drive depreciation are Accounts' (owner, 2026-10-19); Engineering keeps the rest.
  "assets.finance": "Register an asset and set its cost, purchase date and expected life (the Chief Accountant)",
  "assets.verify": "The yearly physical check of the assets: found or not, their condition (the Auditor, with the custodian)",
  "energy.record": "Record meter readings, generator hours and diesel dips",
  "energy.view": "View energy and diesel reports",
  "events.view": "View events diary and BEOs",
  "events.manage": "Create and manage event bookings",
  "groups.manage": "Manage group blocks",
  "events.service": "Record the guests actually served at a function (the F&B supervisor on the day)",
  "ar.view": "View accounts receivable",
  "ar.manage": "Manage AR invoices, receipts and credit notes",
  "ar.approve": "Approve a company's credit note, write-off or voided payment within one's limit (never one's own request)",
  "ar.credit_override": "Exceed company credit limits",
  "ar.credit_approve": "Decide company credit asked for by Sales (above the hotel's limit the General Manager decides as well); stop a company's credit",
  "ap.view": "View accounts payable",
  "ap.manage": "Enter vendor bills and payments",
  "ap.approve": "Approve vendor bills and payments",
  "ap.confirm_service": "Confirm a supplier's service or work was received (the head of the department that received it)",
  "ap.send": "Give approved supplier payments to the bank (the payment file) and record a payment the bank returns",
  "gl.view": "View general ledger",
  "gl.journal_create": "Create manual journal entries",
  "gl.journal_post": "Post / reverse journal entries",
  "gl.period_close": "Close accounting periods",
  "gl.accounts_manage": "Manage chart of accounts",
  "bank.reconcile": "Bank accounts and reconciliation",
  "bank.settlements": "Record the card terminals' and Monnify's settlements into the bank (the general cashiers)",
  "card.disputes": "Record card disputes (chargebacks): the provider's notice, the evidence sent and the outcome (the general cashiers, Accounts)",
  "bank.confirm": "Confirm each month-end bank balance from the bank's own app (the Auditor, the owner): never the one who reconciles",
  "bank.view": "See the bank accounts, their reconciliation and the daily terminal match, without changing them (the Auditor, the owner)",
  "security.view": "See security incidents, patrols, the gate log, the watchlist and keys",
  "security.report": "Report a security incident (and follow the ones I reported)",
  "security.patrol": "Record patrol checkpoints",
  "security.gate": "Log visitors, contractors, deliveries and vehicles in and out at the gate",
  "security.keys": "Issue and take back keys and master cards",
  "security.manage": "Close incidents and keep the watchlist (the Chief Security Officer)",
  "gs.view": "See General Services: trips, vehicles, fuel, the grounds and the prayer hall",
  "gs.manage": "Run General Services: the vehicles, their papers and servicing, taking one off the road",
  "transport.book": "Book a trip with the hotel's drivers (guests' transfers and runs, staff runs, errands)",
  "transport.dispatch": "Give trips a vehicle and a driver; agree a complimentary trip",
  "transport.drive": "Drive: my trips, the daily vehicle check and fuel",
  "grounds.work": "Record the gardeners' grounds tasks",
  "prayer.manage": "Set the iqama times and record the prayer hall checks (the imam)",
  "mgmt.view": "See the management reports: the daily report and the monthly owner's report",
  "mgmt.manage": "Run the GM's desk: the manager-on-duty rota, meetings and actions, notices and policies, the hotel's licences",
  "mgmt.directives": "Log the Chairman's instructions and follow them up",
  "it.view": "See the IT page: the helpdesk, IT equipment, internet outages, the morning check and system health",
  "it.work": "Work IT tickets, keep the IT equipment and what is lent out, record internet outages and the morning check",
  "it.logins": "Unlock and reset the passwords of ordinary staff (never managers, finance, HR, IT or the administrator)",
  "it.manage": "Run IT: licences and renewals with their costs, the monthly access review and data breaches",
  "tax.remit": "Record what is paid to the tax and pension authorities (VAT, consumption tax, withholding tax, PAYE, pension) and void a wrong entry",
  "pettycash.record": "Pay from the petty cash float on a voucher with its receipt",
  "pettycash.approve": "Approve or reject petty cash vouchers and top up the float",
  "pettycash.count": "Count the petty cash by surprise, with the custodian present (the Auditor)",
  "budget.manage": "Prepare the annual budget",
  "budget.approve": "Approve the annual budget",
  "capital.approve": "Approve each item of the capital budget (equipment, vehicles, furniture, building works): the owner",
  "nightaudit.run": "Run night audit",
  // The pack made at each night audit (owner, 2026-10-29): the night auditor, the FOM, the GM, the Chief Accountant, the
  // Auditor and the Chairman.
  "nightaudit.report.view": "Open and print the night audit report",
  "nightaudit.early": "Allow the night audit before its earliest time, with a reason (the GM)",
  "audit.signoff": "Sign off each closed day after checking it: the morning income audit (the Auditor; never the one who closed the day)",
  "reports.frontoffice": "Front office reports",
  "reports.financial": "Financial reports",
  "reports.management": "Management & statistics reports",
  "reports.payments": "The payments report by method and cashier (the general cashiers)",
  "reports.moneywatch": "The owner's weekly money watch: every naira paid out or changing hands (the owner and his representative)",
  "pack.prepare": "Prepare the owner's monthly pack: the closed month's statements, every large difference from budget explained (the Chief Accountant)",
  "pack.review": "Review the owner's monthly pack and issue it to the owner, or send it back (the GM)",
  "reports.nightaudit": "The night audit's own reports: payments, guest and deposit ledgers, refunds and allowances, the rate check (not the hotel's statements)",
  "kpi.view": "View performance targets against actual results: one's own department's measures",
  "kpi.hotel": "Every department's targets, the whole hotel's (the GM, the Chairman, the Auditor)",
  "kpi.manage": "Set performance targets",
  "attendance.clock": "Clock in and out of shifts",
  "attendance.manage": "Manage duty rosters and review attendance",
  "hr.view": "See the staff directory and employee files (not pay, bank or ID numbers)",
  "hr.manage": "Add employees and keep their files, checklists, leave and clock-in codes",
  "hr.sensitive": "See and change NIN, bank, pension and tax numbers",
  "hr.establishment": "Set the approved posts in each department and approve vacancies",
  "hr.team": "See your own department's staff, their leave and who is away",
  "hr.exit": "Ask for a suspension and record leaving dates (a last day less than a week away, or already past, waits for the general manager)",
  "hr.exit_approve": "Decide HR's requests to suspend someone or to end their work within the week (the Chairman decides for the GM and his assistant)",
  "leave.request": "Ask for leave",
  "leave.approve": "Approve or refuse leave for your department",
  // Leave is decided by the person's head, or the HR Manager; the HR Officer records it but decides nothing (owner, 2026-10-19).
  "leave.decide_any": "Approve, refuse or cancel any department's leave (the HR Manager)",
  "laundry.view": "See guest laundry orders, house linen and the laundry figures",
  "laundry.linen": "See house linen going to the laundry and coming back (housekeeping: the laundry is its own department)",
  "laundry.guest": "See guests' laundry orders and claims, to answer guests (front desk: the laundry is its own department)",
  "laundry.collect": "Collect a guest's laundry from the room (housekeeping tablet)",
  "laundry.work": "Count guest laundry in (charging the folio), mark it ready and delivered; record linen in and out, loads and uniforms",
  "laundry.manage": "Decide damage and loss claims, cancel orders, write off linen and post the linen count",
  "laundry.figures.view": "The laundry's figures: revenue, claims, linen lost, chemicals, productivity (the manager, not the floor)",
  "payroll.view": "See the payroll: every run, each person's pay and payslips",
  "payroll.manage": "Set pay, loans and advances and each month's inputs; prepare and submit the payroll",
  "payroll.approve": "Approve or send back a submitted payroll at your step",
  "payroll.post": "Post an approved payroll to the books, record it paid, download the bank and statutory files",
  "sales.view": "See the sales pipeline, accounts, contracts, promotions, campaigns and sales figures",
  "sales.work": "Add leads, log calls, visits and follow-ups, and draft contracts and campaigns",
  "sales.manage": "Give leads and accounts to a salesperson, set sales targets, send contracts, rate changes and promotions to the GM, ask Accounts for company credit, send marketing campaigns",
  "sales.approve": "Approve or send back corporate contracts, rate changes, promotions and large company credit (General Manager)",
  "channel.manage": "Channel manager mapping and sync",
  "booking_engine.manage": "Booking engine settings",
  "admin.users": "Manage users",
  "admin.roles": "Manage roles and permissions",
  "admin.settings": "Property settings, taxes, transaction codes",
  "admin.integrations": "Integration credentials",
  "admin.audit_log": "View audit trail",
  "audit.trail": "Read the audit trail, read-only, by date, person, document or table, and export it (the Auditor; staff files, pay and security left out)",
  "privacy.manage": "Handle data-protection requests (export or anonymise guest data)"
};
var ALL_PERMISSIONS = Object.keys(PERMISSIONS);
var pick = (prefixes, extra = []) => Array.from(/* @__PURE__ */ new Set([...ALL_PERMISSIONS.filter((p) => prefixes.some((x) => p === x || p.startsWith(`${x}.`))), ...extra]));
var VIEW_ALL = ALL_PERMISSIONS.filter((p) => p.endsWith(".view"));
var FRONT_DESK = [
  "reservations.view",
  "reservations.create",
  "reservations.edit",
  "reservations.cancel",
  "reservations.assign_room",
  "frontdesk.checkin",
  "frontdesk.checkout",
  "frontdesk.room_move",
  "frontdesk.key_issue",
  "frontdesk.messages",
  "frontdesk.safe_box",
  "frontdesk.upgrade",
  "guests.view",
  "guests.edit",
  "guests.view_id_documents",
  "companies.view",
  "folios.view",
  "folios.post_charge",
  "folios.post_payment",
  "folios.reverse",
  "folios.transfer",
  "folios.route",
  "folios.close",
  "rates.view",
  "rooms.view",
  "housekeeping.view",
  "housekeeping.rush",
  "engineering.view",
  "engineering.create",
  "reports.frontoffice",
  "frontdesk.emergency_list",
  "laundry.guest",
  "shift.close",
  "lostfound.log",
  "security.report",
  "transport.book",
  "service.view",
  "service.log",
  "service.work",
  "attendance.clock"
  // Not the desk's (owner, 2026-10-17): table bookings are the restaurant's (a guest's request goes to F&B through Guest
  // Service) and the events pages, with their prices and deposits, are Sales'; the desk sees today's functions on its own page.
];
var MONEY_PERMISSIONS = [
  ...ALL_PERMISSIONS.filter((p) => /^(folios|payments|fx|ar|ap|gl|bank|tax|pettycash|budget|payroll|pos|pack|card)\./.test(p)),
  "reports.financial",
  "reports.payments",
  "inventory.purchase",
  "inventory.approve",
  "rates.manage",
  "rates.comp.approve",
  "rates.house.approve",
  "reservations.override_rate",
  "frontdesk.prices",
  "frontdesk.waive_fees",
  "sales.approve",
  "purchase.approve"
];
var HEAD = ["kpi.view", "attendance.clock", "attendance.manage", "hr.team", "leave.approve", "inventory.request_approve", "ap.confirm_service"];
var MFA_ROLES = ["SYSADMIN"];
var PROTECTED_ROLES = ["SYSADMIN", "GM", "OWNER", "FOM", "FC", "ACCT", "GCASH", "INCAUD", "HRM", "CHAIRSA"];
var SHIFT_FREE_ROLES = ["OWNER", "SYSADMIN", "CHEF"];
var shiftFree = (roles) => roles.every((r) => SHIFT_FREE_ROLES.includes(r));
var SCREEN_SCOPE = { DRINKS: { kitchen: "ROOF", station: "BAR" } };
function screenScope(roles, permissions) {
  const has = (p) => Array.isArray(permissions) ? permissions.includes(p) : permissions.has(p);
  if (has("kitchen.view") || has("fnb.view")) return null;
  for (const r of roles) if (SCREEN_SCOPE[r]) return SCREEN_SCOPE[r];
  return null;
}
var NOT_EMPLOYEE_ROLES = ["CHEF", "OWNER"];
var ROLES = [
  // Everything, money included, for now (owner, 2026-10-13: "Admin should see everything and everywhere"); to make it
  // technical only again, filter out MONEY_PERMISSIONS. Staff ID, bank and pension numbers stay visible while the
  // system beds in (owner, 2026-10-08; review a few weeks after go-live); every look is logged.
  { code: "SYSADMIN", name: "System Administrator", department: "IT", permissions: [...ALL_PERMISSIONS] },
  // Approvals within the limits and the management reports; users and roles are the administrator's (owner, 2026-10-12),
  // and so is the audit trail, for now (owner, 2026-10-13: the System Administrator alone). The weekly money watch is
  // the owner's and his representative's, never the GM's (2026-10-14).
  { code: "GM", name: "General Manager", department: "EXEC", permissions: ALL_PERMISSIONS.filter((p) => ![
    "admin.integrations",
    "admin.users",
    "admin.roles",
    "admin.audit_log",
    "audit.trail",
    "reports.moneywatch",
    "capital.approve",
    "pack.prepare",
    "ap.send",
    "audit.signoff",
    "bank.confirm",
    "inventory.review",
    // The approvals, not the Storekeeper's, Purchasing's or the Auditor's own work (owner, 2026-10-26): whoever approves
    // a requisition never issues it, never orders, receives or counts.
    "inventory.issue",
    "inventory.purchase",
    "inventory.count",
    "inventory.witness",
    // The tills' printed slips go to the General Cashier, not the GM, who approves (owner, 2026-10-28).
    "shift.slips",
    // The Auditor's check of a purchase request comes before the GM's approval, never by him (owner, 2026-10-31).
    "purchase.check",
    // He asks the stores like any department; the Storekeeper raises the purchase request (owner, 2026-11-03).
    "purchase.raise",
    // He may cover for his staff when they are off, as long as a second person still checks the work; these five he
    // could finish alone (owner, 2026-11-01, the GM's screen checked): confirming a transfer arrived (the fake-alert check
    // stays with the cashier and Accounts), staff NIN and salary bank accounts, adding staff, setting pay, posting the
    // payroll and its bank file. He keeps approving the payroll at his step.
    "payments.verify_transfers",
    "hr.sensitive",
    "hr.manage",
    "payroll.manage",
    "payroll.post",
    // His own bill's free bill is the Chairman's to decide (owner, 2026-11-02).
    "pos.comp_gm"
  ].includes(p)) },
  // The chairman sees everything and changes nothing, except approving the payroll (the last step, 2026-10-08) and deciding
  // HR's requests to suspend or end the work of the GM and his own assistant (2026-10-10).
  // The top approvals (supplier payments and orders above the GM's limit, owner 2026-10-12), and the GM's own
  // requisitions (2026-10-19). The audit trail, given on 2026-10-12, is the System Administrator's alone for now (owner,
  // 2026-10-13).
  { code: "OWNER", name: "Owner / Chairman", department: "EXEC", permissions: [...VIEW_ALL, "reports.frontoffice", "reports.financial", "reports.management", "kpi.hotel", "payroll.approve", "budget.approve", "mgmt.directives", "hr.exit_approve", "ap.approve", "ar.approve", "inventory.approve", "inventory.issue_approve", "reports.moneywatch", "capital.approve", "gl.journal_post", "bank.confirm", "purchase.approve", "pos.comp_gm"] },
  // The Chairman's office (2026-10-10): the Chairman's reports and his instructions followed up; no approvals, no staff pay or personal data.
  { code: "CHAIRSA", name: "Special Assistant to the Chairman", department: "EXEC", permissions: ["mgmt.view", "mgmt.directives", "attendance.clock"] },
  {
    code: "FOM",
    name: "Front Office Manager",
    department: "FO",
    permissions: pick(["reservations", "frontdesk", "guests", "folios"], [
      ...FRONT_DESK,
      "dashboard.view",
      "rates.house.approve",
      "rooms.ooo",
      "nightaudit.run",
      "nightaudit.report.view",
      "reports.management",
      "groups.manage",
      "service.manage",
      "shift.review",
      // General Services has no supervisor post: the manager assigns drivers and agrees free trips (vehicles' papers, fuel and figures stay theirs).
      // No stores (nobody in Front Office requests stock) and no complimentary rates (there are none: the Chairman,
      // 2026-10-15): owner, 2026-10-17.
      "transport.dispatch",
      ...HEAD.filter((p) => p !== "inventory.request_approve")
      // A refund another way (the guest's card or account no longer works) is the GM's alone (owner, 2026-10-14). The
      // upgrade and lost key card prices are the GM's, as every price (owner, 2026-10-31); the manager sets up the boxes.
    ]).filter((p) => p !== "folios.refund_other" && p !== "folios.let_go_owing" && p !== "frontdesk.prices")
  },
  // The post is Guest Service Agent, the international title for the front desk (owner, 2026-10-18: best practice).
  { code: "GSA", name: "Guest Service Agent (Front Desk)", department: "FO", permissions: FRONT_DESK },
  // Added by the administrator to the receptionists who work nights (2026-10-08): they run the 02:00 audit.
  // Corrections to earlier days and exchange rates stay with the Front Office Manager and Accounts. The night's own
  // reports only, not the hotel's statements, revenue or taxes (owner, 2026-10-13).
  // Every till shift close and the day's reconciliation, read only, to reconcile the cashiers before closing the day
  // (owner, 2026-10-30, the night auditor's checklist); reviewing them stays with the FOM and the Chief Accountant.
  { code: "NA", name: "Night Auditor (night receptionist)", department: "FO", permissions: [...FRONT_DESK, "nightaudit.run", "nightaudit.report.view", "reports.nightaudit", "shift.view"] },
  {
    code: "EHK",
    name: "Executive Housekeeper",
    department: "HK",
    // Reviewed 2026-10-11 with the owner (docs/DEPARTMENT_REVIEW.md): housekeeping's own work, the room rack, its own requests
    // and logbook, faults to engineering, house linen, supplies from the stores, and its own staff. Nothing hotel-wide.
    permissions: pick(["housekeeping", "lostfound"], [
      "rooms.view",
      "rooms.ooo",
      "engineering.view",
      "engineering.create",
      "laundry.linen",
      "laundry.collect",
      // Nobody else in housekeeping asks the stores to buy: the head's own purchase requests go to the GM (owner, 2026-10-18).
      "service.log",
      "service.work",
      "inventory.request",
      ...HEAD.filter((p) => p !== "inventory.request_approve")
    ])
  },
  {
    code: "HKS",
    name: "Housekeeping Supervisor",
    department: "HK",
    permissions: [
      "housekeeping.view",
      "housekeeping.update_status",
      "housekeeping.assign",
      "housekeeping.inspect",
      "housekeeping.post_minibar",
      "housekeeping.rush",
      "lostfound.manage",
      "rooms.view",
      "engineering.view",
      "engineering.create",
      "service.log",
      "service.work",
      "attendance.clock",
      "laundry.linen",
      "laundry.collect"
    ]
  },
  { code: "RA", name: "Room Attendant", department: "HK", permissions: ["housekeeping.view", "housekeeping.update_status", "lostfound.log", "engineering.create", "service.work", "attendance.clock", "laundry.collect"] },
  {
    // The laundry and its own team only (2026-10-08): billing is done by counting a bag in, so no till; no guest
    // database, room rack or equipment register (the laundry machines are on the Laundry page); its own targets only.
    // Laundry reviewed with the owner 2026-10-12 (docs/DEPARTMENT_REVIEW.md): the laundry's own guest requests only (no
    // hotel-wide service.view); found items logged straight into lost & found (pockets, sheets), not the register.
    code: "LDYMGR",
    name: "Laundry Manager",
    department: "LDY",
    // Stores: chemicals asked for, purchase requests, spoiled stock recorded; not the whole hotel's stock, orders or value
    // (owner, 2026-10-18: "same as housekeeping").
    permissions: [
      "service.log",
      "service.work",
      "engineering.create",
      "inventory.request",
      "inventory.waste",
      "lostfound.log",
      "laundry.view",
      "laundry.collect",
      "laundry.work",
      "laundry.manage",
      "laundry.figures.view",
      ...HEAD
    ]
  },
  // The assistant runs the floor; claims and compensation, write-offs, voids, the figures and leave stay with the manager.
  {
    code: "LDYSUP",
    name: "Assistant Laundry Manager",
    department: "LDY",
    permissions: ["service.log", "service.work", "engineering.create", "inventory.request", "attendance.clock", "lostfound.log", "laundry.view", "laundry.collect", "laundry.work"]
  },
  { code: "LDYATT", name: "Laundry Attendant", department: "LDY", permissions: ["service.work", "attendance.clock", "lostfound.log", "laundry.view", "laundry.collect", "laundry.work"] },
  // Security (2026-10-09): officers patrol, keep the gate and keys, and report incidents; the Chief Security Officer
  // closes them and keeps the watchlist; gate keepers only log the gate. All have the evacuation list.
  // No room rack and no emergency list (owner, 2026-10-31, as for the Chief Security Officer): the front desk keeps them.
  { code: "SECURITY", name: "Security Officer", department: "SEC", permissions: ["service.view", "service.log", "service.work", "lostfound.manage", "engineering.create", "attendance.clock", "security.view", "security.report", "security.patrol", "security.gate", "security.keys"] },
  // Reviewed with the owner 2026-10-31 (docs/DEPARTMENT_REVIEW.md): Security's own guest requests, not every department's
  // (no `service.view`); he asks the stores for supplies (torches, batteries, uniforms) and approves no one's requests (his
  // team has no login). He types up the officers' paper gate, patrol and key books. No room rack and no emergency list
  // (owner, 2026-10-31): in an emergency or a locked-out guest he works with the front desk, which keeps them.
  { code: "CSO", name: "Chief Security Officer", department: "SEC", permissions: ["ap.confirm_service", "service.log", "service.work", "lostfound.manage", "lostfound.dispose", "engineering.create", "security.view", "security.report", "security.patrol", "security.gate", "security.keys", "security.manage", "inventory.request", ...HEAD.filter((p) => p !== "inventory.request_approve")] },
  { code: "GATE", name: "Gate Keeper", department: "SEC", permissions: ["security.gate", "security.report", "service.log", "attendance.clock"] },
  // General Services (2026-10-09): the drivers, the gardeners and the hotel imam. The department has no head of its own:
  // the GM runs it, and the Front Office Manager dispatches the drivers (most trips are guests').
  { code: "DRIVER", name: "Driver", department: "GS", permissions: ["transport.drive", "service.work", "attendance.clock"] },
  { code: "GARDENER", name: "Gardener", department: "GS", permissions: ["grounds.work", "attendance.clock"] },
  { code: "IMAM", name: "Hotel Imam", department: "GS", permissions: ["prayer.manage", "attendance.clock"] },
  // IT (2026-10-10): the owner handles IT as the System Administrator; the IT officers work the helpdesk, the equipment
  // and the morning check, and help ordinary staff with their logins. They never hold the administrator's access.
  // IT asks the stores like every department, and raises its own purchase requests for its equipment (owner, 2026-11-03).
  { code: "ITOFF", name: "IT Officer", department: "IT", permissions: ["it.view", "it.work", "it.logins", "service.view", "service.work", "inventory.request", "purchase.raise", "attendance.clock"] },
  {
    code: "CENG",
    name: "Chief Engineer",
    department: "ENG",
    // Keeps the asset register; the yearly physical check is the Auditor's, never the register keeper's (2026-10-13).
    // Not the GM's approval of the kitchen's equipment work orders (owner, 2026-10-25): engineering does the work.
    // Reviewed with the owner 2026-10-31 (docs/DEPARTMENT_REVIEW.md): no hotel dashboard (payments, the guest ledger, room
    // revenue: Maintenance → Today is his start), no housekeeping board (the room rack and out of order stay), maintenance's
    // own guest requests only (not every department's), and no stock costs, values or suppliers (he asks for spares).
    permissions: pick(["engineering", "energy"], ["ap.confirm_service", "assets.view", "assets.manage", "maintenance.view", "rooms.view", "rooms.ooo", "service.log", "service.work", "inventory.request", "inventory.waste", ...HEAD]).filter((p) => p !== "engineering.approve")
  },
  // His jobs, the plant rounds, the room checks and the daily readings, without money (owner, 2026-10-31): not the head's
  // board, contracts or figures (`maintenance.view`), no repair or equipment costs, no stock costs or suppliers.
  { code: "TECH", name: "Maintenance Technician", department: "ENG", permissions: ["engineering.view", "engineering.create", "engineering.work", "rooms.view", "service.work", "assets.view", "energy.record", "inventory.request", "attendance.clock"] },
  // F&B (owner, 2026-10-20): the restaurants, the rooftop restaurant and bar, room service and banquets; the pool and
  // the gym are the GM's. The F&B Manager is the only one with the dashboard. He proposes menu prices (the GM approves)
  // and asks for free bills like anyone (the GM approves); he sees his own department's guest requests and stores.
  {
    code: "FBM",
    name: "F&B Manager",
    department: "FNB",
    // No guest database or guest bills (owner, 2026-10-20): F&B finds a guest by room at the till, diets show in the
    // kitchen office and on the ticket; room-charge disputes are the front desk's and Accounts'. Found items are logged.
    // Screen check (owner, 2026-10-23): F&B -> Today is his dashboard (with the house forecast), not the rooms' dashboard
    // or the rooms' reports; no kitchen display (the kitchen moves the tickets); his own purchase requests go to the GM
    // (nobody under him raises one), so no head's approval.
    permissions: [...pick(["pos"], ["ap.confirm_service", "events.view", "events.service", "fnb.view", "fnb.bookings", "fnb.breakfast", "fnb.outlets", "kitchen.view", "kitchen.cost", "service.log", "service.work", "inventory.request", "inventory.waste", "lostfound.log", ...HEAD])].filter((p) => !["pos.comp", "pos.comp_gm", "pos.price_approve", "pos.kitchen", "inventory.request_approve"].includes(p))
  },
  // The F&B Supervisor works offline (owner, 2026-10-20): a login only to approve at the till (voids of what the kitchen
  // already has, discounts within his limit, room charges above the limit, outside hours), on every shift and on record,
  // never on a bill he opened himself (owner, 2026-11-01). A paid bill is never reopened. No dashboard, reports, menus or
  // stores.
  {
    code: "OUTSUP",
    name: "F&B Supervisor (till approvals)",
    department: "FNB",
    permissions: ["pos.use", "pos.void_item", "pos.discount", "pos.after_hours", "pos.room_charge_over_limit", "attendance.clock", "shift.close"]
  },
  // Waiters and drinks makers log what guests leave at the tables (owner, 2026-10-20); the register stays with housekeeping and security.
  // They log a guest's complaint or request at the table (owner, 2026-10-25): it reaches the F&B Manager in Guest Service.
  // Each team on its own tills (owner, 2026-10-25): the main team the Golden Triumph Restaurant, in-room dining and
  // banquets; the rooftop waiters the rooftop restaurant; the bartenders the bar (worksOutlet in pos.service).
  // The breakfast buffet is at the main restaurant: its team checks guests in, not the rooftop's or the bar's (2026-10-25).
  { code: "CASHIER", name: "Waiter / Waitress (Main)", department: "FNB", permissions: ["pos.use", "fnb.bookings", "fnb.breakfast", "service.log", "service.work", "lostfound.log", "attendance.clock", "shift.close"] },
  { code: "ROOFWAIT", name: "Waiter / Waitress (Rooftop)", department: "FNB", permissions: ["pos.use", "fnb.bookings", "service.log", "service.work", "lostfound.log", "attendance.clock", "shift.close"] },
  // The pool and the gym are in the GM's portfolio (owner, 2026-10-20): the attendants keep the desk and its tills; the GM
  // closes, reopens and decides what the rooftop supervisor did.
  // The gym instructor (owner, 2026-10-27): the pool's money and desk moved to the rooftop till (the bartender).
  { code: "LEISURE", name: "Gym Instructor", department: "EXEC", permissions: ["leisure.view", "leisure.desk", "pos.use", "service.work", "attendance.clock", "shift.close"] },
  // The bar's staff (owner, 2026-10-24, global name; the bar serves no alcohol): the till, and the bar's orders on the screen.
  // The rooftop's one till person on duty (owner, 2026-10-27): every rooftop order, food, grills and drinks, and the pool.
  { code: "DRINKS", name: "Bartender", department: "FNB", permissions: ["pos.use", "pos.kitchen", "fnb.bookings", "service.log", "service.work", "lostfound.log", "attendance.clock", "shift.close", "leisure.pool"] },
  // A screen in a kitchen, not a person (no employee file): the orders and dishes sold out, nothing else.
  { code: "CHEF", name: "Kitchen Display Screen", department: "KIT", permissions: ["pos.kitchen"] },
  // The kitchen is one brigade under the Executive Chef (2026-10-07; global names 2026-10-24): the head of the main
  // kitchen and of both. Under her, the Head Chef of the rooftop kitchen and the Sous Chefs who lead the main kitchen's
  // shifts she is not on, each with their own login (no one signs in as another, owner 2026-10-24). Cooks, bakers, commis,
  // helpers and stewards clock in at the entrance tablet and cook from the printed ticket; they need no login. They ask the
  // stores for what they need and record spoiled food; the whole hotel's stock is not theirs (2026-10-20).
  // The kitchen's review (owner, 2026-10-25): the head chefs and sous chefs cook from the recipes without their costs (the
  // costs stay with the Executive Chef, the F&B Manager and Accounts); the kitchen reports its equipment faults, which
  // wait for the GM's approval unless urgent.
  { code: "EXCHEF", name: "Executive Chef", department: "KIT", permissions: ["ap.confirm_service", "pos.kitchen", "kitchen.view", "kitchen.log", "kitchen.recipes", "kitchen.cost", "engineering.create", "inventory.request", "inventory.waste", "service.work", ...HEAD] },
  { code: "HEADCHEF", name: "Head Chef / Sous Chef", department: "KIT", permissions: ["pos.kitchen", "kitchen.view", "kitchen.log", "engineering.create", "inventory.request", "inventory.waste", "service.work", "attendance.clock"] },
  // Buys for the hotel (2026-10-09): raises purchase orders and pays small market purchases from petty cash.
  // The buyer never records the cash paid out (owner, 2026-11-01): petty cash is the General Cashier's; market buying goes
  // through a cash advance he pays out. No vehicle booking (General Services) either.
  { code: "PURCH", name: "Purchasing Officer", department: "FIN", permissions: ["inventory.view", "inventory.purchase", "inventory.request", "ap.view", "attendance.clock"] },
  // Holds the stock (2026-10-13): receives and issues, records waste, asks for restock; orders nothing and counts nothing.
  // Keeps the item list too (the organisation's decision, owner 2026-10-27): every change shows on the money watch.
  { code: "STORE", name: "Storekeeper", department: "FIN", permissions: ["inventory.view", "inventory.issue", "inventory.waste", "inventory.request", "inventory.manage", "purchase.raise", "attendance.clock"] },
  {
    // Sells rooms, groups and events and looks after the accounts (2026-10-08): proposes rates, promotions and
    // contracts for the GM to approve and asks Accounts for credit; takes no money and changes no price itself.
    code: "SALES",
    name: "Head of Sales & Marketing",
    department: "SAL",
    // No Revenue Manager or Reservations Agent at the hotel (owner, 2026-10-12): online selling (channel manager, the
    // website's booking engine) is Sales & Marketing's; prices stay with the GM, bookings with the front desk.
    // Reviewed with the owner 2026-10-30 (docs/DEPARTMENT_REVIEW.md): bookings are the desk's (she sees them, books none);
    // guest profiles read only (consent is the guest's, recorded by the desk and the booking engine); the number served
    // at a function is F&B's (it is billed); the event's own bill comes with events, not every guest's bill; no cash or
    // guest-ledger figures (the hotel dashboard), her accounts' standing on her own Accounts tab instead of the ledger.
    // The room rack, read only, for show-rounds (owner, 2026-10-31): which rooms are clean and free to show a client.
    permissions: pick(["companies", "events", "groups"], [
      "channel.manage",
      "booking_engine.manage",
      "sales.view",
      "sales.work",
      "sales.manage",
      "reservations.view",
      "guests.view",
      "rates.view",
      "rooms.view",
      "reports.management",
      ...HEAD
    ]).filter((p) => p !== "events.service")
  },
  {
    code: "SALEX",
    name: "Sales & Marketing Assistant",
    department: "SAL",
    permissions: ["sales.view", "sales.work", "companies.view", "events.view", "reservations.view", "guests.view", "rates.view", "attendance.clock"]
  },
  // Finance reviewed with the owner 2026-10-12/13 (docs/DEPARTMENT_REVIEW.md). Since 2026-10-19 (owner) one Chief
  // Accountant replaces the Financial Controller and the two Accountants. The Financial Controller's role stays, with no
  // post, in case one is hired later: the second key within the limits (accounts.approval_limits).
  {
    code: "FC",
    name: "Financial Controller",
    department: "FIN",
    permissions: [
      "gl.view",
      "gl.journal_create",
      "gl.journal_post",
      "gl.period_close",
      "gl.accounts_manage",
      "pack.prepare",
      "ar.view",
      "ar.manage",
      "ar.approve",
      "ar.credit_approve",
      "ar.credit_override",
      "ap.view",
      "ap.manage",
      "ap.approve",
      "bank.reconcile",
      "tax.remit",
      "fx.manage",
      "reports.financial",
      "reports.management",
      "dashboard.view",
      "folios.view",
      "companies.view",
      "guests.view",
      "inventory.view",
      "inventory.request",
      "inventory.approve",
      "inventory.review",
      "inventory.manage",
      "assets.view",
      "assets.finance",
      "payroll.view",
      "payroll.post",
      "pettycash.approve",
      "budget.manage",
      "kitchen.cost",
      "energy.view",
      "shift.review",
      "card.disputes",
      ...HEAD.filter((p) => p !== "ap.confirm_service")
    ]
  },
  {
    // The Chief Accountant (owner, 2026-10-19), head of Accounts, answering to the GM. She prepares everything: bills,
    // company invoices, receipts, journals, supplier payments and the bank file, the reconciliations, taxes, payroll
    // posting, the budget, the month-end close and the owner's pack. The GM approves what she prepares (journals,
    // bills, payments, credit notes, write-offs, reconciliations) up to his limit, the Chairman above. She approves only
    // what others prepare, up to her limit: Purchasing's orders, the cashiers' petty cash, the stores' waste and
    // corrections; and she reviews each department's requisition before the GM approves it.
    code: "ACCT",
    name: "Chief Accountant",
    department: "FIN",
    permissions: [
      "gl.view",
      "gl.journal_create",
      "gl.period_close",
      "gl.accounts_manage",
      "pack.prepare",
      "ar.view",
      "ar.manage",
      "ar.credit_approve",
      "ap.view",
      "ap.manage",
      "ap.send",
      "bank.reconcile",
      "tax.remit",
      "fx.manage",
      "reports.financial",
      "nightaudit.report.view",
      "reports.management",
      "dashboard.view",
      "folios.view",
      "companies.view",
      "guests.view",
      "inventory.view",
      "inventory.request",
      "inventory.approve",
      "inventory.review",
      "inventory.manage",
      "assets.view",
      "assets.finance",
      "payroll.view",
      "payroll.post",
      "pettycash.approve",
      "budget.manage",
      "kitchen.cost",
      "energy.view",
      "shift.review",
      "card.disputes",
      ...HEAD.filter((p) => p !== "ap.confirm_service")
    ]
  },
  {
    // The two Cashier posts in a cashless hotel: the petty cash float, the card and Monnify settlements against the
    // day's batches, each day's transfers confirmed, the daily payments report.
    code: "GCASH",
    name: "General Cashier",
    department: "FIN",
    // Every till shift close read only and each shift's printed slips received (owner, 2026-10-28).
    // The petty cash counted each day, the card disputes, the transfers confirmed against the statement (owner, 2026-10-30).
    permissions: ["pettycash.record", "payments.verify_transfers", "bank.settlements", "reports.payments", "attendance.clock", "shift.view", "shift.slips", "card.disputes"]
  },
  {
    // Independent (owner, 2026-10-13): answers to the Chairman, sees everything and changes nothing, except the stock
    // counts it leads as the hotel's cost control (blind, the storekeeper present).
    code: "INCAUD",
    name: "Auditor",
    department: "EXEC",
    // Everything an audit traces money through, but not staff files, pay or the security log and watchlist: her work
    // needs none of them (owner 2026-10-16). She clocks in like all staff, and reads the lost & found register (owner,
    // 2026-10-19).
    permissions: [
      ...VIEW_ALL.filter((p) => !["hr.view", "payroll.view", "security.view"].includes(p)),
      "reports.frontoffice",
      "reports.financial",
      "reports.management",
      "inventory.count",
      "inventory.witness",
      "assets.verify",
      "audit.signoff",
      "bank.confirm",
      "pettycash.count",
      "lostfound.view",
      "kpi.hotel",
      "attendance.clock",
      // The audit trail, read-only (owner, 2026-10-26): the company auditor's note expects her to trace every change.
      "audit.trail",
      // The cost controller's check of each purchase request, as on the hotel's Purchase Request book (owner, 2026-10-31).
      "purchase.check"
    ]
  },
  // HR decides who works here (employee files, posts filled, leave, clock-in codes); it creates no logins
  // beyond a post's own role, never touches rooms or money, and only the HR Manager sees ID, bank and pension numbers.
  // HR sees HR only (owner, 2026-10-10): no hotel dashboard, targets, management reports, trip booking or stores. It
  // confirms only the supplier work HR itself received, a training course or a recruitment agency (owner, 2026-10-16).
  // Asks for suspensions and records leaving; never decides alone what stops someone working (the owner, 10 October 2026).
  { code: "HRM", name: "HR Manager", department: "HR", permissions: [
    "hr.view",
    "hr.manage",
    "hr.sensitive",
    "hr.exit",
    "payroll.view",
    "payroll.manage",
    "leave.decide_any",
    ...HEAD.filter((p) => !["kpi.view", "inventory.request_approve"].includes(p))
  ] },
  { code: "HRO", name: "HR Officer", department: "HR", permissions: ["hr.view", "hr.manage", "hr.team", "attendance.clock", "attendance.manage"] }
];
var LOGBOOK_ALSO = ["EXCHEF", "HEADCHEF", "ITOFF"];
var NO_LOGBOOK = ["CASHIER", "ROOFWAIT", "DRINKS"];
var keepsLogbook = (r) => !NO_LOGBOOK.includes(r.code) && (r.permissions.includes("service.log") || r.permissions.includes("service.manage") || LOGBOOK_ALSO.includes(r.code));
var DEFAULT_ROLES = ROLES.map((r) => ({
  ...r,
  permissions: [.../* @__PURE__ */ new Set([
    ...r.permissions,
    ...r.permissions.includes("attendance.clock") ? ["leave.request"] : [],
    ...keepsLogbook(r) ? ["service.logbook"] : []
  ])]
}));

// src/money.ts
import Decimal from "decimal.js";
var D = Decimal.clone({ precision: 34, rounding: Decimal.ROUND_HALF_UP });
var dec = (v) => new D(v ?? 0);
var round2 = (v) => dec(v).toDecimalPlaces(2, D.ROUND_HALF_UP);
var m2 = (v) => round2(v).toFixed(2);
var sum = (values) => values.reduce((acc, v) => acc.plus(dec(v)), dec(0));
var isZero = (v) => dec(v).isZero();
var CURRENCY_SYMBOL = { NGN: "\u20A6", USD: "$", GBP: "\xA3", EUR: "\u20AC" };
function formatMoney(value, currency = "NGN", opts = {}) {
  const d = round2(value);
  const negative = d.isNegative();
  const [int = "0", frac = "00"] = d.abs().toFixed(2).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const sym = opts.symbol === false ? "" : CURRENCY_SYMBOL[currency] ?? `${currency} `;
  return `${negative ? "-" : ""}${sym}${grouped}.${frac}`;
}
var toBase = (amount, rate) => m2(dec(amount).times(dec(rate)));
var fromBase = (amount, rate) => m2(dec(amount).dividedBy(dec(rate)));

// src/tax.ts
function orderedRules(rules, category, transactionCode) {
  const applicable = rules.filter(
    (r) => r.active && r.appliesTo.includes(category) && !dec(r.percent).isZero() && !(transactionCode && r.exemptCodes?.includes(transactionCode))
  );
  const byCode = new Map(applicable.map((r) => [r.code, r]));
  const done = /* @__PURE__ */ new Set();
  const out = [];
  const visit = (r, stack) => {
    if (done.has(r.code)) return;
    if (stack.has(r.code)) throw new Error(`Circular tax compounding involving ${r.code}`);
    stack.add(r.code);
    for (const dep of r.compoundOn) {
      const d = byCode.get(dep);
      if (d) visit(d, stack);
    }
    stack.delete(r.code);
    done.add(r.code);
    out.push(r);
  };
  [...applicable].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code)).forEach((r) => visit(r, /* @__PURE__ */ new Set()));
  return out;
}
function taxesOnNet(net, rules, rounded) {
  const result = /* @__PURE__ */ new Map();
  for (const r of rules) {
    const base = r.compoundOn.reduce((acc, code2) => acc.plus(result.get(code2)?.amount ?? 0), net);
    const raw = base.times(dec(r.percent)).dividedBy(100);
    result.set(r.code, { base, amount: rounded ? round2(raw) : raw });
  }
  return result;
}
function pricesIncludeTaxesFor(category, property, settings) {
  return property.pricesIncludeTaxes || (settings?.taxesIncludedIn ?? []).includes(category);
}
function computeTaxes(amount, category, rules, pricesIncludeTaxes = false, transactionCode) {
  const applicable = orderedRules(rules, category, transactionCode);
  const input = round2(amount);
  let net;
  let lines;
  if (!pricesIncludeTaxes || applicable.length === 0) {
    net = input;
    lines = taxesOnNet(net, applicable, true);
  } else {
    const unit = taxesOnNet(new D(1), applicable, false);
    const factor = sum([...unit.values()].map((v) => v.amount)).plus(1);
    net = round2(input.dividedBy(factor));
    lines = taxesOnNet(net, applicable, true);
    const diff = input.minus(net).minus(sum([...lines.values()].map((v) => v.amount)));
    net = net.plus(diff);
  }
  const taxes = applicable.map((r) => {
    const l = lines.get(r.code);
    return { code: r.code, name: r.name, base: m2(l.base), percent: dec(r.percent).toString(), amount: m2(l.amount) };
  });
  const totalTax = sum(taxes.map((t) => t.amount));
  return { net: m2(net), taxes, totalTax: m2(totalTax), gross: m2(net.plus(totalTax)) };
}

// src/dates.ts
var ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
function isIsoDate(v) {
  if (typeof v !== "string" || !ISO_DATE.test(v)) return false;
  const d = /* @__PURE__ */ new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}
var toUtc = (d) => {
  if (!isIsoDate(d)) throw new Error(`Invalid ISO date: ${d}`);
  return Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
};
var fromUtc = (ms) => new Date(ms).toISOString().slice(0, 10);
var DAY = 864e5;
var addDays = (d, n) => fromUtc(toUtc(d) + n * DAY);
var diffDays = (a, b) => Math.round((toUtc(b) - toUtc(a)) / DAY);
function eachDay(from, toExclusive) {
  const out = [];
  for (let t = toUtc(from), end = toUtc(toExclusive); t < end; t += DAY) out.push(fromUtc(t));
  return out;
}
var dayOfWeek = (d) => new Date(toUtc(d)).getUTCDay();
function todayIn(timeZone = "Africa/Lagos", now = /* @__PURE__ */ new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const get = (t) => parts.find((p) => p.type === t).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}
function tzOffsetMinutes(at, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }).formatToParts(at);
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  return (Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second")) - at.getTime()) / 6e4;
}
function zonedDateTime(date, time2, timeZone = "Africa/Lagos") {
  const hhmm4 = time2.slice(0, 5);
  const guess = /* @__PURE__ */ new Date(`${date}T${hhmm4}:00Z`);
  return new Date(guess.getTime() - tzOffsetMinutes(guess, timeZone) * 6e4);
}
function formatDate(d, style = "short") {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "2-digit",
    month: style === "long" ? "long" : "short",
    year: "numeric",
    ...style === "long" ? { weekday: "short" } : {}
  }).format(new Date(toUtc(d)));
}

// src/hr.ts
function leaveDays(from, to, counting, holidays = [], workWeekDays = 6) {
  if (to < from) return 0;
  const off = new Set(holidays);
  let days2 = 0;
  let holidayDays = 0;
  for (let d = from; d <= to; d = addDays(d, 1)) {
    days2++;
    if (off.has(d)) holidayDays++;
  }
  if (counting === "CALENDAR_DAYS") return days2;
  const restDays = Math.floor(days2 / 7) * Math.max(0, 7 - workWeekDays);
  return Math.max(0, days2 - holidayDays - restDays);
}
function annualAllowance(fullDays, hireDate, leaveYear, yearStartMonth = 1) {
  const start = `${leaveYear}-${String(yearStartMonth).padStart(2, "0")}-01`;
  if (hireDate <= start) return fullDays;
  const [hy, hm] = hireDate.split("-").map(Number);
  const monthsIn = (hy - leaveYear) * 12 + (hm - yearStartMonth);
  if (monthsIn >= 12) return 0;
  return Math.round(fullDays * (12 - monthsIn) / 12 * 2) / 2;
}
var REVIEW_CRITERIA = [
  { code: "JOB_KNOWLEDGE", label: "Job knowledge and skills" },
  { code: "QUALITY", label: "Quality and accuracy of work" },
  { code: "GUEST_SERVICE", label: "Guest service and courtesy" },
  { code: "ATTENDANCE", label: "Attendance and punctuality" },
  { code: "TEAMWORK", label: "Teamwork and cooperation" },
  { code: "INITIATIVE", label: "Initiative and reliability" },
  { code: "GROOMING", label: "Grooming, hygiene and safety" },
  { code: "CONDUCT", label: "Conduct and the hotel's rules" },
  { code: "LEADERSHIP", label: "Leading and developing the team", optional: true }
];
var RATING_LABELS = { 1: "Poor", 2: "Below expectations", 3: "Meets expectations", 4: "Exceeds expectations", 5: "Outstanding" };
function overallRating(ratings) {
  const v = Object.values(ratings).filter((n) => typeof n === "number");
  return v.length ? Math.round(v.reduce((a, n) => a + n, 0) / v.length * 10) / 10 : 0;
}
function dayOffWindow(day, firstWindowStart, weeks = 4) {
  const len = weeks * 7;
  const index = Math.floor(diffDays(firstWindowStart, day) / len);
  const start = addDays(firstWindowStart, index * len);
  return { start, end: addDays(start, len - 1), index };
}
var dayOffAllowance = (workWeekDays, weeks = 4) => Math.max(0, 7 - workWeekDays) * weeks;

// src/payroll.ts
var NIGERIA_PAYROLL_2026 = {
  minimumWageMonthly: 7e4,
  pension: { employeePercent: 8, employerPercent: 10 },
  nhfPercent: 2.5,
  nsitfPercent: 1,
  itfPercent: 1,
  rentRelief: { percent: 20, max: 5e5 },
  taxBands: [
    { upTo: 8e5, rate: 0 },
    { upTo: 3e6, rate: 15 },
    { upTo: 12e6, rate: 18 },
    { upTo: 25e6, rate: 21 },
    { upTo: 5e7, rate: 23 },
    { upTo: null, rate: 25 }
  ]
};
var PAY_COMPONENTS = ["basic", "housing", "transport", "meal", "utility", "other"];
var PAY_COMPONENT_LABEL = {
  basic: "Basic salary",
  housing: "Housing allowance",
  transport: "Transport allowance",
  meal: "Meal allowance",
  utility: "Utility allowance",
  other: "Other allowances"
};
var pct = (base, percent) => round2(base.times(percent).dividedBy(100));
function yearlyTax(taxable, bands = NIGERIA_PAYROLL_2026.taxBands) {
  let left = dec(taxable);
  let floor = dec(0);
  let tax = dec(0);
  for (const b of bands) {
    if (left.lte(0)) break;
    const width = b.upTo === null ? left : dec(b.upTo).minus(floor);
    const slice = left.lt(width) ? left : width;
    tax = tax.plus(slice.times(b.rate).dividedBy(100));
    left = left.minus(slice);
    if (b.upTo !== null) floor = dec(b.upTo);
  }
  return round2(tax);
}
function computePay(input, rules = NIGERIA_PAYROLL_2026) {
  const warnings = [];
  const share = input.daysInPeriod > 0 ? dec(Math.max(0, Math.min(input.daysPaid, input.daysInPeriod))).dividedBy(input.daysInPeriod) : dec(0);
  const components = Object.fromEntries(PAY_COMPONENTS.map((c) => [c, round2(dec(input.pay[c]).times(share))]));
  const regular = sum(Object.values(components));
  const earnings = input.earnings ?? [];
  const overtime = sum(earnings.filter((e2) => e2.kind === "OVERTIME").map((e2) => e2.amount));
  const extra = sum(earnings.filter((e2) => e2.kind !== "OVERTIME").map((e2) => e2.amount));
  const oneOff = overtime.plus(extra);
  const gross = regular.plus(oneOff);
  const pensionable = components.basic.plus(components.housing).plus(components.transport);
  const pensionEmployee = input.pensionMember === false ? dec(0) : pct(pensionable, rules.pension.employeePercent);
  const pensionEmployer = input.pensionMember === false ? dec(0) : pct(pensionable, rules.pension.employerPercent);
  const nhf = input.nhfMember === false ? dec(0) : pct(components.basic, rules.nhfPercent);
  const contractGross = sum(PAY_COMPONENTS.map((c) => input.pay[c]));
  const exempt = contractGross.lte(rules.minimumWageMonthly);
  const rentRelief = dec(input.annualRent ?? 0).times(rules.rentRelief.percent).dividedBy(100);
  const reliefs = {
    pension: pensionEmployee.times(12),
    nhf: nhf.times(12),
    rent: round2(rentRelief.gt(rules.rentRelief.max) ? dec(rules.rentRelief.max) : rentRelief)
  };
  const yearlyRegular = regular.times(12);
  const yearlyTaxable = max0(yearlyRegular.minus(reliefs.pension).minus(reliefs.nhf).minus(reliefs.rent));
  const taxRegular = exempt ? dec(0) : yearlyTax(yearlyTaxable, rules.taxBands);
  const taxWithOneOff = exempt ? dec(0) : yearlyTax(yearlyTaxable.plus(oneOff), rules.taxBands);
  const oneOffTax = round2(taxWithOneOff.minus(taxRegular));
  const paye = round2(taxRegular.dividedBy(12).plus(oneOffTax));
  let left = gross.minus(pensionEmployee).minus(nhf).minus(paye);
  const applied = [];
  const ordered = [...(input.deductions ?? []).filter((d) => d.kind === "DEDUCTION"), ...(input.deductions ?? []).filter((d) => d.kind === "LOAN")];
  for (const d of ordered) {
    const want = round2(d.amount);
    const take = want.gt(left) ? max0(left) : want;
    if (take.lt(want)) warnings.push(`${d.description ?? (d.kind === "LOAN" ? "Instalment" : "Deduction")}: only ${m2(take)} of ${m2(want)} could be taken`);
    if (take.gt(0)) applied.push({ kind: d.kind, amount: m2(take), description: d.description, ref: d.ref });
    left = left.minus(take);
  }
  const loanDeductions = sum(applied.filter((a) => a.kind === "LOAN").map((a) => a.amount));
  const otherDeductions = sum(applied.filter((a) => a.kind === "DEDUCTION").map((a) => a.amount));
  if (loanDeductions.plus(otherDeductions).gt(gross.dividedBy(3))) warnings.push("Loans and deductions take more than a third of the pay");
  const totalDeductions = pensionEmployee.plus(nhf).plus(paye).plus(loanDeductions).plus(otherDeductions);
  const net = gross.minus(totalDeductions);
  const nsitf = pct(gross, rules.nsitfPercent);
  const itf = pct(gross, rules.itfPercent);
  if (exempt && contractGross.gt(0)) warnings.push("At or below the national minimum wage: no PAYE");
  if (contractGross.gt(0) && contractGross.lt(rules.minimumWageMonthly)) warnings.push(`Monthly pay is below the national minimum wage (${m2(rules.minimumWageMonthly)})`);
  return {
    components: Object.fromEntries(PAY_COMPONENTS.map((c) => [c, m2(components[c])])),
    overtime: m2(overtime),
    extraEarnings: m2(extra),
    gross: m2(gross),
    pensionEmployee: m2(pensionEmployee),
    nhf: m2(nhf),
    paye: m2(paye),
    loanDeductions: m2(loanDeductions),
    otherDeductions: m2(otherDeductions),
    totalDeductions: m2(totalDeductions),
    net: m2(net),
    pensionEmployer: m2(pensionEmployer),
    nsitf: m2(nsitf),
    itf: m2(itf),
    employerCost: m2(gross.plus(pensionEmployer).plus(nsitf).plus(itf)),
    applied,
    tax: {
      exemptMinimumWage: exempt,
      yearlyRegularGross: m2(yearlyRegular),
      reliefs: { pension: m2(reliefs.pension), nhf: m2(reliefs.nhf), rent: m2(reliefs.rent) },
      yearlyTaxable: m2(yearlyTaxable),
      yearlyTax: m2(taxRegular),
      oneOffTaxable: m2(oneOff),
      oneOffTax: m2(oneOffTax)
    },
    warnings
  };
}
function max0(v) {
  return v.isNegative() ? dec(0) : v;
}
function splitGross(gross, split) {
  const g = dec(gross);
  const parts = Object.fromEntries(PAY_COMPONENTS.map((c) => [c, round2(g.times(split[c] ?? 0).dividedBy(100))]));
  parts.basic = parts.basic.plus(g.minus(sum(Object.values(parts))));
  return Object.fromEntries(PAY_COMPONENTS.map((c) => [c, m2(parts[c])]));
}
function periodDays(period, hireDate, lastDay) {
  const [y, m] = period.split("-").map(Number);
  const from = `${period}-01`;
  const days2 = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const to = `${period}-${String(days2).padStart(2, "0")}`;
  const start = hireDate > from ? hireDate : from;
  const end = lastDay && lastDay < to ? lastDay : to;
  const employed = start > end ? 0 : Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 864e5) + 1;
  return { from, to, days: days2, employed };
}

// src/laundry.ts
var WAT_OFFSET_MS = 60 * 6e4;
var minutes = (hhmm4) => {
  const [h, m] = hhmm4.split(":").map(Number);
  return h * 60 + m;
};
function laundryDueAt(collectedAt, t, express) {
  if (express) return new Date(collectedAt.getTime() + t.expressHours * 36e5);
  const local = new Date(collectedAt.getTime() + WAT_OFFSET_MS);
  const minuteOfDay = local.getUTCHours() * 60 + local.getUTCMinutes();
  if (minuteOfDay <= minutes(t.cutOff)) return new Date(collectedAt.getTime() + t.hours * 36e5);
  const nextCutOff = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() + 1) + minutes(t.cutOff) * 6e4 - WAT_OFFSET_MS;
  return new Date(nextCutOff + t.hours * 36e5);
}
function laundryHours(categories, serviceHours, fallback = 8) {
  const hours = categories.map((c) => serviceHours[c]).filter((h) => typeof h === "number" && h > 0);
  return hours.length ? Math.max(...hours) : fallback;
}
function linenRequired(par, roomsByType, parLevel) {
  const out = {};
  for (const p of par) out[p.linenCode] = (out[p.linenCode] ?? 0) + p.pieces * (roomsByType[p.roomTypeId] ?? 0) * parLevel;
  return out;
}

// src/contracts/common.ts
import { z } from "zod";
var isoDate = z.string().refine(isIsoDate, "Must be a valid date (YYYY-MM-DD)");
var money = z.union([z.string(), z.number()]).transform((v) => String(v)).refine((v) => /^-?\d+(\.\d{1,2})?$/.test(v), "Invalid amount");
var positiveMoney = money.refine((v) => Number(v) > 0, "Amount must be greater than zero");
var nonNegativeMoney = money.refine((v) => Number(v) >= 0, "Amount cannot be negative");
var uuid = z.uuid();
var code = z.string().trim().min(1).max(20).regex(/^[A-Z0-9_\-]+$/, "Use capitals, digits, - or _");
var optionalText = (max = 500) => z.string().trim().max(max).transform((v) => v ? v : void 0).optional();
var pagination = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50)
});

// src/contracts/auth.ts
import { z as z2 } from "zod";
var loginSchema = z2.object({
  username: z2.string().trim().min(1).max(64),
  password: z2.string().min(1).max(256)
});
var passwordPolicy = z2.string().min(10, "At least 10 characters").max(128).regex(/[a-z]/, "Needs a lowercase letter").regex(/[A-Z]/, "Needs an uppercase letter").regex(/\d/, "Needs a digit");
var changePasswordSchema = z2.object({
  currentPassword: z2.string().min(1),
  newPassword: passwordPolicy
}).refine((v) => v.currentPassword !== v.newPassword, { message: "New password must differ", path: ["newPassword"] });
var mfaVerifySchema = z2.object({ mfaToken: z2.string().min(20).max(2e3), code: z2.string().trim().min(6).max(12) });
var mfaCodeSchema = z2.object({ code: z2.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code") });
var mfaDisableSchema = z2.object({ password: z2.string().min(1).max(256), code: z2.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code") });
var pinSchema = z2.string().regex(/^\d{4,6}$/, "Use 4 to 6 digits");
var pinLoginSchema = z2.object({ deviceToken: z2.string().min(20).max(200), userId: z2.uuid(), pin: pinSchema });
var deviceRosterSchema = z2.object({ deviceToken: z2.string().min(20).max(200) });
var setPinSchema = z2.object({ password: z2.string().min(1).max(256), pin: pinSchema });

// src/contracts/profiles.ts
import { z as z3 } from "zod";
var phone = z3.string().trim().max(32).regex(/^[+\d][\d\s\-()]{5,}$/, "Invalid phone number");
var country = z3.string().trim().length(2).transform((v) => v.toUpperCase());
var addressSchema = z3.object({
  line1: optionalText(200),
  line2: optionalText(200),
  city: optionalText(100),
  state: optionalText(100),
  country: country.optional(),
  postalCode: optionalText(20)
}).default({});
var guestInputSchema = z3.object({
  title: optionalText(20),
  firstName: z3.string().trim().min(1).max(80),
  middleName: optionalText(80),
  lastName: z3.string().trim().min(1).max(80),
  gender: z3.enum(GENDERS).optional(),
  dateOfBirth: isoDate.optional(),
  nationality: country.optional(),
  email: z3.email().max(160).optional().or(z3.literal("").transform(() => void 0)),
  phone: phone.optional().or(z3.literal("").transform(() => void 0)),
  whatsapp: phone.optional().or(z3.literal("").transform(() => void 0)),
  address: addressSchema,
  occupation: optionalText(100),
  companyId: uuid.optional(),
  vipCode: optionalText(10),
  preferences: z3.array(z3.string().max(40)).max(30).default([]),
  /** Dietary needs and allergies the kitchen must know (codes from hotel.yaml `kitchen.dietary`). */
  dietary: z3.array(z3.string().max(30)).max(20).default([]),
  dietaryNote: optionalText(300),
  notes: optionalText(2e3),
  idDocType: optionalText(20),
  idDocNumber: optionalText(40),
  idDocIssuingCountry: country.optional(),
  idDocExpiry: isoDate.optional(),
  visaNumber: optionalText(40),
  visaExpiry: isoDate.optional(),
  marketingConsent: z3.boolean().default(false)
});
var guestUpdateSchema = guestInputSchema.partial().extend({ address: addressSchema.optional(), preferences: z3.array(z3.string().max(40)).max(30).optional(), dietary: z3.array(z3.string().max(30)).max(20).optional() });
var guestSearchSchema = z3.object({
  q: z3.string().trim().max(100).optional(),
  page: z3.coerce.number().int().min(1).default(1),
  pageSize: z3.coerce.number().int().min(1).max(100).default(25)
});
var blacklistSchema = z3.object({ blacklisted: z3.boolean(), reason: optionalText(500) });
var companyInputSchema = z3.object({
  name: z3.string().trim().min(2).max(160),
  type: z3.enum(COMPANY_TYPES),
  tin: optionalText(30),
  rcNumber: optionalText(30),
  email: z3.email().optional().or(z3.literal("").transform(() => void 0)),
  phone: phone.optional().or(z3.literal("").transform(() => void 0)),
  address: addressSchema,
  contactName: optionalText(120),
  contactEmail: z3.email().optional().or(z3.literal("").transform(() => void 0)),
  contactPhone: phone.optional().or(z3.literal("").transform(() => void 0)),
  iataCode: optionalText(20),
  commissionPercent: z3.coerce.number().min(0).max(50).optional(),
  arEnabled: z3.boolean().default(false),
  creditLimit: nonNegativeMoney.default("0"),
  paymentTermsDays: z3.coerce.number().int().min(0).max(180).default(30),
  defaultRatePlanId: uuid.optional(),
  marketSegmentCode: optionalText(10),
  ratePlanIds: z3.array(uuid).default([]),
  notes: optionalText(2e3),
  isActive: z3.boolean().default(true)
});

// src/contracts/reservations.ts
import { z as z4 } from "zod";
var time = z4.string().regex(/^\d{2}:\d{2}$/, "Use HH:MM").optional();
var ack = z4.array(z4.string().max(60)).max(20).default([]);
var BILLING_OPTIONS = ["GUEST", "COMPANY_ROOM_TAX", "COMPANY_ALL"];
var stayFields = {
  arrivalDate: isoDate,
  departureDate: isoDate,
  adults: z4.coerce.number().int().min(1).max(12),
  // room types enforce their own limits (Presidential wing: 10)
  children: z4.coerce.number().int().min(0).max(6).default(0),
  roomTypeId: uuid,
  ratePlanId: uuid
};
var detailFields = {
  companyId: uuid.optional(),
  travelAgentId: uuid.optional(),
  groupBlockId: uuid.optional(),
  marketSegmentCode: optionalText(10),
  sourceCode: z4.string().trim().min(1).max(20),
  channel: z4.enum(CHANNELS).default("DIRECT"),
  externalRef: optionalText(60),
  rateOverride: z4.object({ amount: nonNegativeMoney, reason: z4.string().trim().min(3).max(300) }).optional(),
  /** House use (staff and management): who is staying and why; recorded with the Front Office Manager's approval. */
  houseUseReason: optionalText(300),
  eta: time,
  etd: time,
  arrivalTransport: optionalText(120),
  departureTransport: optionalText(120),
  purposeOfVisit: optionalText(120),
  comingFrom: optionalText(120),
  nextDestination: optionalText(120),
  vipCode: optionalText(10),
  specialRequests: optionalText(2e3),
  internalNotes: optionalText(2e3),
  paymentMethodCode: optionalText(20),
  billing: z4.enum(BILLING_OPTIONS).default("GUEST"),
  bookerName: optionalText(120),
  bookerEmail: z4.email().optional().or(z4.literal("").transform(() => void 0)),
  bookerPhone: optionalText(32)
};
var createReservationSchema = z4.object({
  ...stayFields,
  ...detailFields,
  roomId: uuid.optional(),
  guestId: uuid.optional(),
  guest: guestInputSchema.optional(),
  sendConfirmation: z4.boolean().default(true),
  overbook: z4.boolean().default(false),
  acknowledge: ack
}).refine((v) => v.departureDate > v.arrivalDate, { message: "Departure must be after arrival", path: ["departureDate"] }).refine((v) => !!v.guestId !== !!v.guest, { message: "Select an existing guest or enter a new one", path: ["guest"] });
var updateReservationSchema = z4.object({
  version: z4.number().int().min(1),
  arrivalDate: isoDate.optional(),
  departureDate: isoDate.optional(),
  adults: z4.coerce.number().int().min(1).max(12).optional(),
  children: z4.coerce.number().int().min(0).max(6).optional(),
  roomTypeId: uuid.optional(),
  ratePlanId: uuid.optional(),
  rateOverride: detailFields.rateOverride.nullable(),
  houseUseReason: optionalText(300),
  companyId: uuid.nullable().optional(),
  travelAgentId: uuid.nullable().optional(),
  marketSegmentCode: optionalText(10),
  sourceCode: z4.string().trim().min(1).max(20).optional(),
  eta: time,
  etd: time,
  arrivalTransport: optionalText(120),
  departureTransport: optionalText(120),
  purposeOfVisit: optionalText(120),
  comingFrom: optionalText(120),
  nextDestination: optionalText(120),
  vipCode: optionalText(10),
  specialRequests: optionalText(2e3),
  internalNotes: optionalText(2e3),
  paymentMethodCode: optionalText(20),
  bookerName: optionalText(120),
  bookerEmail: detailFields.bookerEmail,
  bookerPhone: optionalText(32),
  overbook: z4.boolean().default(false),
  acknowledge: ack
});
var cancelReservationSchema = z4.object({
  reason: z4.string().trim().min(3).max(500),
  waivePenalty: z4.boolean().default(false)
});
var assignRoomSchema = z4.object({ roomId: uuid, acknowledge: ack });
var reservationSearchSchema = z4.object({
  q: z4.string().trim().max(100).optional(),
  status: z4.union([z4.enum(RESERVATION_STATUSES), z4.array(z4.enum(RESERVATION_STATUSES))]).optional(),
  arrivalFrom: isoDate.optional(),
  arrivalTo: isoDate.optional(),
  departureFrom: isoDate.optional(),
  departureTo: isoDate.optional(),
  stayDate: isoDate.optional(),
  companyId: uuid.optional(),
  groupBlockId: uuid.optional(),
  view: z4.enum(["all", "arrivals", "departures", "inhouse"]).default("all"),
  page: z4.coerce.number().int().min(1).default(1),
  pageSize: z4.coerce.number().int().min(1).max(200).default(50)
});
var quoteSchema = z4.object({
  ...stayFields,
  ratePlanId: uuid.optional(),
  companyId: uuid.optional(),
  channel: z4.enum(CHANNELS).default("DIRECT")
}).partial({ roomTypeId: true }).refine((v) => v.departureDate > v.arrivalDate, { message: "Departure must be after arrival", path: ["departureDate"] });
var checkInSchema = z4.object({ roomId: uuid.optional(), acknowledge: ack, keyCards: z4.coerce.number().int().min(0).max(6).default(2), upgrade: z4.boolean().default(false) });
var checkOutSchema = z4.object({
  acknowledge: ack,
  sendFolio: z4.boolean().default(true),
  /** Key cards handed back at the desk (left out: all of them). */
  keyCardsReturned: z4.coerce.number().int().min(0).max(50).optional(),
  /** The manager does not charge the lost cards: why. */
  waiveLostCards: z4.string().trim().min(3).max(300).optional()
});
var roomMoveSchema = z4.object({ roomId: uuid, reason: z4.string().trim().min(3).max(300), acknowledge: ack });

// src/contracts/folios.ts
import { z as z5 } from "zod";
var postChargeSchema = z5.object({
  transactionCode: z5.string().trim().min(1).max(10),
  unitPrice: positiveMoney,
  quantity: z5.coerce.number().positive().max(999).default(1),
  description: optionalText(200),
  reference: optionalText(60),
  // A possible duplicate (the same charge and amount on the stay's bills today) is posted once confirmed (owner, 2026-10-30).
  acknowledge: z5.array(z5.string()).default([])
});
var postPaymentSchema = z5.object({
  method: z5.string().trim().min(1).max(20),
  amount: positiveMoney,
  currency: z5.string().length(3).default("NGN"),
  reference: optionalText(80),
  terminal: optionalText(20),
  notes: optionalText(300)
});
var refundSchema = z5.object({
  originalPaymentId: uuid,
  amount: positiveMoney,
  reference: optionalText(80),
  terminal: optionalText(20),
  notes: optionalText(300),
  reason: z5.string().trim().min(3).max(300),
  /**
   * The guest's card or account no longer works (owner, 2026-10-14): the GM alone may send it another way, to an
   * account in the guest's own name, with the reason above. It is flagged in the refunds report and the money watch.
   */
  anotherWay: z5.object({
    method: z5.string().trim().min(1).max(20),
    accountName: z5.string().trim().min(3).max(120),
    accountNo: z5.string().trim().regex(/^\d{10}$/, "A 10-digit account number"),
    bank: z5.string().trim().min(2).max(80)
  }).optional()
});
var reverseSchema = z5.object({ reason: z5.string().trim().min(3).max(300) });
var allowanceSchema = z5.object({
  transactionCode: z5.string().trim().min(1).max(10),
  amount: positiveMoney,
  reason: z5.string().trim().min(3).max(300),
  againstTransactionId: uuid.optional()
});
var transferSchema = z5.object({ targetFolioId: uuid, reason: optionalText(300) });
var newWindowSchema = z5.object({ payeeType: z5.enum(["GUEST", "COMPANY"]), companyId: uuid.optional(), name: optionalText(160) });
var routingSchema = z5.object({
  targetFolioId: uuid,
  categories: z5.array(z5.string()).default([]),
  transactionCodes: z5.array(z5.string()).default([])
}).refine((v) => v.categories.length + v.transactionCodes.length > 0, "Choose at least one category or code");
var verifyTransferSchema = z5.object({ approve: z5.boolean(), note: optionalText(300), bankAccount: z5.string().trim().max(20).optional(), statementLineId: z5.uuid().optional() });
var letGoSchema = z5.object({ reason: z5.string().trim().min(5).max(500), phone: z5.string().trim().max(30).optional() });
var FRONT_DESK_TERMINALS = "Front Desk";

// src/sales.ts
var LEAD_KINDS = ["CORPORATE", "GROUP", "EVENT", "WEDDING", "GOVERNMENT", "LONG_STAY", "OTHER"];
var LEAD_KIND_LABEL = {
  CORPORATE: "Corporate rooms account",
  GROUP: "Group rooms",
  EVENT: "Conference / event",
  WEDDING: "Wedding / walima",
  GOVERNMENT: "Government / parastatal",
  LONG_STAY: "Long stay",
  OTHER: "Other"
};
var LEAD_SOURCES = ["REFERRAL", "REPEAT", "WALK_IN", "PHONE", "EMAIL", "WHATSAPP", "WEBSITE", "SOCIAL", "COLD_CALL", "SALES_VISIT", "EVENT", "OTHER"];
var LEAD_SOURCE_LABEL = {
  REFERRAL: "Referral",
  REPEAT: "Repeat client",
  WALK_IN: "Walk-in",
  PHONE: "Phone call in",
  EMAIL: "E-mail in",
  WHATSAPP: "WhatsApp in",
  WEBSITE: "Website",
  SOCIAL: "Social media",
  COLD_CALL: "Our call",
  SALES_VISIT: "Our sales visit",
  EVENT: "Met at an event",
  OTHER: "Other"
};
var LEAD_STAGES = ["NEW", "CONTACTED", "PROPOSAL", "NEGOTIATION", "WON", "LOST"];
var OPEN_STAGES = ["NEW", "CONTACTED", "PROPOSAL", "NEGOTIATION"];
var LEAD_STAGE_LABEL = { NEW: "New", CONTACTED: "Contacted", PROPOSAL: "Proposal sent", NEGOTIATION: "Negotiating", WON: "Won", LOST: "Lost" };
var STAGE_PROBABILITY = { NEW: 10, CONTACTED: 25, PROPOSAL: 50, NEGOTIATION: 75, WON: 100, LOST: 0 };
var LOST_REASONS = ["PRICE", "AVAILABILITY", "COMPETITOR", "LOCATION", "FACILITIES", "NO_RESPONSE", "CANCELLED", "OTHER"];
var LOST_REASON_LABEL = {
  PRICE: "Price",
  AVAILABILITY: "No rooms or hall on their dates",
  COMPETITOR: "Went to a competitor",
  LOCATION: "Location",
  FACILITIES: "Facilities",
  NO_RESPONSE: "Stopped answering",
  CANCELLED: "Their plans were cancelled",
  OTHER: "Other"
};
var ACTIVITY_KINDS = ["CALL", "VISIT", "MEETING", "EMAIL", "WHATSAPP", "SITE_INSPECTION", "PROPOSAL", "FOLLOW_UP", "OTHER"];
var ACTIVITY_KIND_LABEL = {
  CALL: "Call",
  VISIT: "Sales visit",
  MEETING: "Meeting",
  EMAIL: "E-mail",
  WHATSAPP: "WhatsApp",
  SITE_INSPECTION: "Show-round / site inspection",
  PROPOSAL: "Proposal sent",
  FOLLOW_UP: "Follow-up",
  OTHER: "Other"
};
function canMoveStage(from, to) {
  if (from === to) return false;
  if (OPEN_STAGES.includes(from)) return true;
  return to === "CONTACTED";
}
function weightedPipeline(leads) {
  return Math.round(leads.filter((l) => OPEN_STAGES.includes(l.stage)).reduce((s, l) => s + Number(l.expectedRevenue) * STAGE_PROBABILITY[l.stage] / 100, 0) * 100) / 100;
}
var CAMPAIGN_FIELDS = ["first_name", "full_name", "hotel", "promo_code"];
function personalise(text, values) {
  return text.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (all, key) => values[key] ?? all);
}
var PROMO_CODE = /^[A-Z0-9]{3,20}$/;

// src/frontoffice.ts
var minutes2 = (hhmm4) => {
  const [h, m] = hhmm4.split(":").map(Number);
  return h * 60 + m;
};
function hotelMinutes(at) {
  const local = new Date(at.getTime() + 36e5);
  return local.getUTCHours() * 60 + local.getUTCMinutes();
}
function earlyCheckInFee(minuteOfDay, p) {
  if (minuteOfDay >= minutes2(p.earlyFreeFrom)) return null;
  if (minuteOfDay < minutes2(p.earlyFrom)) return { percent: 100, rule: `a full night (arrival before ${p.earlyFrom})` };
  return { percent: p.earlyPercent, rule: `${p.earlyPercent}% of a night (check-in before ${p.earlyFreeFrom})` };
}
function lateCheckOutFee(minuteOfDay, p) {
  if (minuteOfDay <= minutes2(p.lateFreeUntil)) return null;
  if (minuteOfDay <= minutes2(p.lateUntil)) return { percent: p.latePercent, rule: `${p.latePercent}% of a night (check-out after ${p.lateFreeUntil}, by ${p.lateUntil})` };
  return { percent: 100, rule: `a full night (check-out after ${p.lateUntil})` };
}
function hotelDate(at) {
  return new Date(at.getTime() + 36e5).toISOString().slice(0, 10);
}
function auditAllowed(businessDate, at, earliest) {
  const today = hotelDate(at);
  return today > businessDate || today === businessDate && hotelMinutes(at) >= minutes2(earliest);
}
function auditLate(businessDate, at, alertAt) {
  const today = hotelDate(at);
  const yesterday = hotelDate(new Date(at.getTime() - 864e5));
  return businessDate < yesterday || businessDate === yesterday && hotelMinutes(at) >= minutes2(alertAt);
}
function noShowDue(businessDate, at, noShowTime) {
  const today = hotelDate(at);
  return today > businessDate || today === businessDate && hotelMinutes(at) >= minutes2(noShowTime);
}
function sameDayCancellationPercent(tiers, arrivalDate, today, minuteOfDay) {
  if (!tiers.length || today < arrivalDate) return 0;
  const sorted = [...tiers].sort((a, b) => minutes2(a.from) - minutes2(b.from));
  if (today > arrivalDate) return sorted[sorted.length - 1].percent;
  return sorted.filter((t) => minutes2(t.from) <= minuteOfDay).reduce((pct2, t) => t.percent, 0);
}
function sameDayMoveFee(tiers, movePercent, arrivalDate, today, minuteOfDay) {
  if (!movePercent || !tiers.length) return null;
  const pct2 = sameDayCancellationPercent(tiers, arrivalDate, today, minuteOfDay);
  if (pct2 === 0) return null;
  return pct2 >= 100 ? "NO_SHOW" : { percent: movePercent };
}
function netPromoterScore(scores) {
  if (!scores.length) return null;
  const promoters = scores.filter((s) => s >= 9).length;
  const detractors = scores.filter((s) => s <= 6).length;
  return Math.round((promoters - detractors) / scores.length * 100);
}
var TURNAWAY_REASONS = ["FULL", "ROOM_TYPE", "PRICE", "RESTRICTION", "LOCATION", "OTHER"];
var TURNAWAY_REASON_LABEL = {
  FULL: "Hotel full",
  ROOM_TYPE: "Room type wanted was full",
  PRICE: "Price",
  RESTRICTION: "Minimum stay or closed date",
  LOCATION: "Location",
  OTHER: "Other"
};
var ALERT_MOMENTS = ["CHECK_IN", "CHECK_OUT", "ALWAYS"];
var ALERT_MOMENT_LABEL = { CHECK_IN: "At check-in", CHECK_OUT: "At check-out", ALWAYS: "Whenever the booking is opened" };
var DESK_MESSAGE_KINDS = ["MESSAGE", "PARCEL"];
var DESK_MESSAGE_LABEL = { MESSAGE: "Message", PARCEL: "Parcel" };
function keyCardSettlement(p) {
  const returned = Math.max(0, Math.min(p.out, Math.floor(p.returned)));
  const lost = p.out - returned;
  const each = p.waived || p.price === null ? dec(0) : dec(p.price);
  return { returned, lost, charge: m2(each.times(lost)) };
}
function upgradeTotal(perNight, nights) {
  return m2(dec(perNight).times(Math.max(0, nights)));
}
var SAFE_BOX_CODE = /^[A-Za-z0-9-]{1,10}$/;

// src/rooftop.ts
var minutesOf = (hhmm4) => Number(hhmm4.slice(0, 2)) * 60 + Number(hhmm4.slice(3, 5));
function openAt(window, minutes3) {
  if (!window) return true;
  const [from, until] = window.split("-");
  const f = minutesOf(from);
  let u = minutesOf(until);
  if (until === "23:59") u = 24 * 60;
  if (f === u || f === 0 && u === 24 * 60) return true;
  return f < u ? minutes3 >= f && minutes3 < u : minutes3 >= f || minutes3 < u;
}
var hhmm = (m) => `${String(Math.floor((m % 1440 + 1440) % 1440 / 60)).padStart(2, "0")}:${String((m % 1440 + 1440) % 1440 % 60).padStart(2, "0")}`;
function overdueToClose(window, minutes3, graceMinutes = 60) {
  if (!window || openAt(window, minutes3)) return false;
  const [from, until] = window.split("-");
  return !openAt(`${from}-${hhmm(minutesOf(until) + graceMinutes)}`, minutes3);
}
function poolTestResult(t, r) {
  const problems = [];
  if (t.ph < r.ph[0]) problems.push(`pH ${t.ph} is below ${r.ph[0]}`);
  if (t.ph > r.ph[1]) problems.push(`pH ${t.ph} is above ${r.ph[1]}`);
  if (t.chlorine < r.chlorine[0]) problems.push(`chlorine ${t.chlorine} mg/l is below ${r.chlorine[0]}`);
  if (t.chlorine > r.chlorine[1]) problems.push(`chlorine ${t.chlorine} mg/l is above ${r.chlorine[1]}`);
  if (!t.clearWater) problems.push("the water is not clear");
  return { passed: problems.length === 0, problems };
}
function membershipEnds(startsOn, months) {
  const [y, m, d] = startsOn.split("-").map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  const end = d > lastDay ? new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), lastDay)) : new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), d - 1));
  return end.toISOString().slice(0, 10);
}
function poolTestsDue(times, testsAt, now) {
  const slots = [...times].sort();
  return slots.map((time2, i) => {
    const from = minutesOf(time2) - 60;
    const until = i + 1 < slots.length ? minutesOf(slots[i + 1]) - 60 : 24 * 60;
    const done = testsAt.some((t) => t >= from && t < until);
    return { time: time2, done, late: !done && now >= minutesOf(time2) + 60, due: now >= from };
  }).filter((s) => s.due).map(({ time: time2, done, late }) => ({ time: time2, done, late }));
}
function poolHoursPaid(hoursBought, persons) {
  return Math.max(1, Math.floor(hoursBought / Math.max(1, persons)));
}
var readingsDue = poolTestsDue;

// src/kitchen.ts
function plateCost(lines, portions) {
  const total = lines.reduce((a, l) => a.plus(dec(l.qty).times(dec(100).dividedBy(dec(100).minus(l.wastePercent))).times(l.unitCost)), dec(0));
  return m2(total.dividedBy(dec(portions).isZero() ? 1 : portions));
}
function costPercent(cost, price) {
  if (!Number(price)) return null;
  return Math.round(Number(cost) / Number(price) * 1e3) / 10;
}
function menuEngineering(items) {
  const out = /* @__PURE__ */ new Map();
  const total = items.reduce((a, i) => a + i.sold, 0);
  if (!items.length || !total) return out;
  const popularAt = 1 / items.length * 0.7;
  const avgMargin = items.reduce((a, i) => a + i.margin * i.sold, 0) / total;
  for (const i of items) {
    const popular = i.sold / total >= popularAt;
    const profitable = i.margin >= avgMargin;
    out.set(i.id, popular && profitable ? "STAR" : popular ? "PLOWHORSE" : profitable ? "PUZZLE" : "DOG");
  }
  return out;
}
var DIETARY_ALLERGENS = {
  NUT_ALLERGY: ["NUTS", "PEANUTS"],
  SHELLFISH_ALLERGY: ["SHELLFISH"],
  FISH_ALLERGY: ["FISH"],
  EGG_ALLERGY: ["EGG"],
  DAIRY_FREE: ["DAIRY"],
  GLUTEN_FREE: ["GLUTEN"],
  VEGAN: ["MEAT", "FISH", "SHELLFISH", "EGG", "DAIRY"],
  VEGETARIAN: ["MEAT", "FISH", "SHELLFISH"]
};
var ALLERGENS = ["GLUTEN", "DAIRY", "EGG", "NUTS", "PEANUTS", "FISH", "SHELLFISH", "SOY", "SESAME", "MEAT"];
function allergyClashes(dietary, allergens) {
  const banned = new Set(dietary.flatMap((d) => DIETARY_ALLERGENS[d] ?? []));
  return allergens.filter((a) => banned.has(a));
}
var temperatureOk = (reading, min, max) => reading >= min && reading <= max;
function ticketAge(sentAt, targetMinutes, now = /* @__PURE__ */ new Date()) {
  const minutes3 = Math.max(0, Math.floor((now.getTime() - new Date(sentAt).getTime()) / 6e4));
  return { minutes: minutes3, late: !!targetMinutes && minutes3 > targetMinutes };
}

// src/accounts.ts
function returnDueDate(periodEnd, dueDay) {
  const [y, m] = periodEnd.split("-").map(Number);
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m, Math.min(dueDay, last))).toISOString().slice(0, 10);
}
function returnStatus(due, paid, dueDate, today) {
  const owed = dec(due).minus(paid);
  if (dec(due).lessThanOrEqualTo(0) && dec(paid).isZero()) return "NIL";
  if (owed.lessThanOrEqualTo("0.01")) return "PAID";
  if (today > dueDate) return "LATE";
  return dec(paid).greaterThan(0) ? "PART_PAID" : "DUE";
}
function monthlyDepreciation(cost, lifeYears, accumulated) {
  if (!(Number(cost) > 0) || !(Number(lifeYears) > 0)) return "0.00";
  const left = dec(cost).minus(accumulated);
  if (left.lessThanOrEqualTo(0)) return "0.00";
  const month = dec(cost).dividedBy(dec(lifeYears).times(12));
  return m2(month.greaterThan(left) ? left : month);
}
function budgetVariance(actual, budget) {
  const v = dec(actual).minus(budget);
  return { variance: m2(v), percent: Number(budget) ? Math.round(Number(v) / Math.abs(Number(budget)) * 1e3) / 10 : null };
}
var RECEIVING_DEPARTMENTS = {
  ROOMS: ["FO", "HK"],
  FNB: ["FNB", "KIT"],
  // The rooftop restaurant and bar are F&B's (2026-10-20); the pool and the gym the GM's (Management, always allowed).
  FNB_ROOF: ["FNB", "KIT"],
  GYM: [],
  LAUNDRY: ["LDY"],
  HLDY: ["LDY"],
  HOUSE_LAUNDRY: ["LDY"],
  TRANSPORT: ["FO", "GS"],
  SDIN: ["KIT"],
  STAFF_DINING: ["KIT"],
  AG: ["FIN", "HR", "SEC", "GS"],
  IT: ["IT"],
  SM: ["SAL"],
  POM: ["ENG"],
  UTIL: ["ENG"]
};
var MANAGEMENT_DEPARTMENT = "EXEC";
function receivingDepartments(lines) {
  let allowed = null;
  for (const l of lines) {
    const these = l.costCenter && (RECEIVING_DEPARTMENTS[l.costCenter] ?? (l.usaliGroup ? RECEIVING_DEPARTMENTS[l.usaliGroup] : void 0)) || [];
    allowed = allowed === null ? [...these] : allowed.filter((d) => these.includes(d));
  }
  return [MANAGEMENT_DEPARTMENT, ...allowed ?? []];
}

// src/maintenance.ts
var days = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / 864e5);
var addDays2 = (d, n) => new Date(Date.parse(d) + n * 864e5).toISOString().slice(0, 10);
function roomCheckDue(lastChecked, today, everyDays) {
  if (!lastChecked) return { dueOn: null, due: true, daysOverdue: 0 };
  const dueOn = addDays2(lastChecked, everyDays);
  return { dueOn, due: dueOn <= today, daysOverdue: Math.max(0, days(dueOn, today)) };
}
function contractStanding(c, today, warningDays) {
  const daysLeft = days(today, c.endsOn);
  const status = today < c.startsOn ? "NOT_STARTED" : daysLeft < 0 ? "EXPIRED" : daysLeft <= warningDays ? "EXPIRING" : "ACTIVE";
  const nextVisit = c.visitEveryDays ? addDays2(c.lastVisitOn ?? c.startsOn, c.visitEveryDays) : null;
  return { status, daysLeft, nextVisit, visitOverdue: !!nextVisit && status !== "EXPIRED" && status !== "NOT_STARTED" && nextVisit < today };
}
function jobCost(parts, labourMinutes, ratePerHour) {
  const labour = dec(labourMinutes ?? 0).dividedBy(60).times(ratePerHour);
  return { parts: m2(parts), labour: m2(labour), total: m2(dec(parts).plus(labour)) };
}

// src/security.ts
var minutesOf2 = (hhmm4) => Number(hhmm4.slice(0, 2)) * 60 + Number(hhmm4.slice(3, 5));
var addDays3 = (d, n) => new Date(Date.parse(d) + n * 864e5).toISOString().slice(0, 10);
function patrolRounds(p) {
  const start = minutesOf2(p.from);
  let end = minutesOf2(p.until);
  if (end <= start) end += 24 * 60;
  const out = [];
  for (let m = start; m < end; m += p.everyMinutes) {
    const t = m % (24 * 60);
    out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
  }
  return out;
}
function patrolRound(date, minutes3, p) {
  const start = minutesOf2(p.from);
  let end = minutesOf2(p.until);
  const crosses = end <= start;
  if (crosses) end += 24 * 60;
  const afterMidnight = crosses && minutes3 < minutesOf2(p.until);
  const night = afterMidnight ? addDays3(date, -1) : date;
  const m = afterMidnight ? minutes3 + 24 * 60 : minutes3;
  if (m < start || m >= end) return { night: crosses && minutes3 < start && !afterMidnight ? date : night, round: null };
  return { night, round: Math.floor((m - start) / p.everyMinutes) };
}
function pastVisitorCutoff(minutes3, cutoff) {
  return minutes3 >= minutesOf2(cutoff) || minutes3 < 6 * 60;
}
var keyOverdue = (issuedAt, hours, now = /* @__PURE__ */ new Date()) => now.getTime() - new Date(issuedAt).getTime() > hours * 36e5;
function idLast4(id) {
  const clean = (id ?? "").replace(/\s+/g, "");
  return clean ? clean.slice(-4).toUpperCase() : null;
}

// src/general.ts
var addDays4 = (d, n) => new Date(Date.parse(d) + n * 864e5).toISOString().slice(0, 10);
var daysBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 864e5);
var minutesOf3 = (hhmm4) => Number(hhmm4.slice(0, 2)) * 60 + Number(hhmm4.slice(3, 5));
var hhmm2 = (m) => {
  const t = (Math.round(m) % 1440 + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
var PRAYERS = ["FAJR", "DHUHR", "ASR", "MAGHRIB", "ISHA"];
var rad = (d) => d * Math.PI / 180;
var deg = (r) => r * 180 / Math.PI;
var fixAngle = (a) => a - 360 * Math.floor(a / 360);
var fixHour = (h) => h - 24 * Math.floor(h / 24);
function julian(date) {
  let y = Number(date.slice(0, 4));
  let m = Number(date.slice(5, 7));
  const d = Number(date.slice(8, 10));
  if (m <= 2) {
    y -= 1;
    m += 12;
  }
  const a = Math.floor(y / 100);
  const b = 2 - a + Math.floor(a / 4);
  return Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + d + b - 1524.5;
}
function sun(jd) {
  const d = jd - 2451545;
  const g = fixAngle(357.529 + 0.98560028 * d);
  const q = fixAngle(280.459 + 0.98564736 * d);
  const l = fixAngle(q + 1.915 * Math.sin(rad(g)) + 0.02 * Math.sin(rad(2 * g)));
  const e2 = 23.439 - 36e-8 * d;
  const ra = deg(Math.atan2(Math.cos(rad(e2)) * Math.sin(rad(l)), Math.cos(rad(l)))) / 15;
  return { declination: deg(Math.asin(Math.sin(rad(e2)) * Math.sin(rad(l)))), equation: q / 15 - fixHour(ra) };
}
function prayerTimes(date, m) {
  const jd = julian(date) - m.longitude / (15 * 24);
  const lat = m.latitude;
  const noon = (t) => fixHour(12 - sun(jd + t / 24).equation);
  const angleTime = (angle, t, before) => {
    const { declination } = sun(jd + t / 24);
    const x = deg(Math.acos((-Math.sin(rad(angle)) - Math.sin(rad(declination)) * Math.sin(rad(lat))) / (Math.cos(rad(declination)) * Math.cos(rad(lat))))) / 15;
    return noon(t) + (before ? -x : x);
  };
  const asrTime = (t) => {
    const { declination } = sun(jd + t / 24);
    const angle = -deg(Math.atan(1 / (m.asrFactor + Math.tan(rad(Math.abs(lat - declination))))));
    return angleTime(angle, t, false);
  };
  const shift = m.utcOffset - m.longitude / 15;
  const at = (h) => hhmm2((h + shift) * 60);
  return {
    fajr: at(angleTime(m.fajrAngle, 5, true)),
    sunrise: at(angleTime(0.833, 6, true)),
    dhuhr: at(noon(12)),
    asr: at(asrTime(13)),
    maghrib: at(angleTime(0.833, 18, false)),
    isha: at(angleTime(m.ishaAngle, 18, false))
  };
}
function qiblaBearing(latitude, longitude) {
  const [kLat, kLon] = [21.4225, 39.8262];
  const dl = rad(kLon - longitude);
  const b = deg(Math.atan2(Math.sin(dl), Math.cos(rad(latitude)) * Math.tan(rad(kLat)) - Math.sin(rad(latitude)) * Math.cos(dl)));
  return Math.round(fixAngle(b));
}
function iqamaTime(adhan, rule) {
  if (!rule) return adhan;
  if (rule.mode === "AT" && rule.at) return minutesOf3(rule.at) >= minutesOf3(adhan) ? rule.at : adhan;
  return hhmm2(minutesOf3(adhan) + (rule.minutes ?? 0));
}
var HIJRI_MONTHS = ["Muharram", "Safar", "Rabi' al-Awwal", "Rabi' al-Thani", "Jumada al-Ula", "Jumada al-Akhirah", "Rajab", "Sha'ban", "Ramadan", "Shawwal", "Dhu al-Qa'dah", "Dhu al-Hijjah"];
function hijriDate(date) {
  try {
    const parts = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", { day: "numeric", month: "numeric", year: "numeric", timeZone: "UTC" }).formatToParts(/* @__PURE__ */ new Date(`${date}T12:00:00Z`));
    const get = (t) => Number(parts.find((p) => p.type === t)?.value.replace(/\D/g, ""));
    const [day, month, year] = [get("day"), get("month"), get("year")];
    if (!day || !month || !year || year > 1700) return null;
    return { day, month, monthName: HIJRI_MONTHS[month - 1], year };
  } catch {
    return null;
  }
}
function paperStanding(until, today, warningDays) {
  if (!until) return "MISSING";
  if (until < today) return "EXPIRED";
  return until <= addDays4(today, warningDays) ? "DUE" : "OK";
}
function serviceDue(v, today) {
  if (v.lastServiceKm === null || !v.lastServiceDate) return { due: true, kmLeft: null, dueDate: null };
  const kmLeft = v.lastServiceKm + v.everyKm - v.odometerKm;
  const d = /* @__PURE__ */ new Date(`${v.lastServiceDate}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + v.everyMonths);
  const dueDate = d.toISOString().slice(0, 10);
  return { due: kmLeft <= 0 || dueDate <= today, kmLeft, dueDate };
}
function fuelEconomy(fills) {
  const out = /* @__PURE__ */ new Map();
  let lastFull = null;
  let litres = 0;
  for (const f of fills) {
    litres += f.litres;
    if (!f.full) continue;
    if (lastFull !== null && f.odometerKm > lastFull && litres > 0) out.set(f.id, Math.round((f.odometerKm - lastFull) / litres * 100) / 100);
    lastFull = f.odometerKm;
    litres = 0;
  }
  return out;
}
function fuelFlag(kmPerLitre, others, variancePercent) {
  if (others.length < 2) return false;
  const s = [...others].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  const usual = s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  return kmPerLitre < usual * (1 - variancePercent / 100);
}
var unloggedKm = (lastIn, out) => lastIn === null ? 0 : Math.max(0, out - lastIn);
function tripsOverlap(aStart, aMinutes, bStart, bMinutes) {
  const a = new Date(aStart).getTime();
  const b = new Date(bStart).getTime();
  return a < b + bMinutes * 6e4 && b < a + aMinutes * 6e4;
}
function taskDue(lastDone, everyDays, today) {
  const dueOn = lastDone ? addDays4(lastDone, everyDays) : today;
  return { dueOn, due: dueOn <= today, lateDays: Math.max(0, daysBetween(dueOn, today)) };
}

// src/it.ts
var ticketDueAt = (at, priority, hours) => new Date(at.getTime() + (hours[priority] ?? 24) * 36e5);
function availability(outages, from, to, now = /* @__PURE__ */ new Date()) {
  if (to.getTime() <= from.getTime()) return { minutesDown: 0, percentUp: 100 };
  const span = to.getTime() - from.getTime();
  let down = 0;
  for (const o of outages) {
    const s = Math.max(new Date(o.startedAt).getTime(), from.getTime());
    const e2 = Math.min(o.endedAt ? new Date(o.endedAt).getTime() : now.getTime(), to.getTime());
    if (e2 > s) down += e2 - s;
  }
  return { minutesDown: Math.round(down / 6e4), percentUp: Math.round((1 - down / span) * 1e4) / 100 };
}
function renewalStanding(renewsOn, today, warningDays) {
  if (!renewsOn) return "NONE";
  if (renewsOn < today) return "OVERDUE";
  const warn = new Date(Date.parse(today) + warningDays * 864e5).toISOString().slice(0, 10);
  return renewsOn <= warn ? "DUE" : "OK";
}
function nextRenewal(renewsOn, billing) {
  if (billing === "ONE_OFF") return null;
  const d = /* @__PURE__ */ new Date(`${renewsOn}T00:00:00Z`);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + (billing === "MONTHLY" ? 1 : billing === "QUARTERLY" ? 3 : 12));
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
}
var BREACH_NOTIFY_HOURS = 72;
function breachClock(discoveredAt, now = /* @__PURE__ */ new Date()) {
  const deadline = new Date(new Date(discoveredAt).getTime() + BREACH_NOTIFY_HOURS * 36e5);
  return { deadline, hoursLeft: Math.round((deadline.getTime() - now.getTime()) / 36e5 * 10) / 10, late: now > deadline };
}
var PROTECTED_PREFIXES = ["admin.", "it.", "hr.", "payroll.", "gl.", "bank.", "privacy.", "budget.", "tax."];
function ordinaryLogin(roles, permissions) {
  if (roles.some((r) => PROTECTED_ROLES.includes(r))) return false;
  for (const p of permissions) if (PROTECTED_PREFIXES.some((x) => p.startsWith(x))) return false;
  return true;
}

// src/management.ts
var minutesOf4 = (hhmm4) => Number(hhmm4.slice(0, 2)) * 60 + Number(hhmm4.slice(3, 5));
function actionStanding(status, dueOn, today) {
  if (status === "DONE") return "DONE";
  if (status === "CANCELLED") return "CANCELLED";
  if (dueOn < today) return "OVERDUE";
  return dueOn === today ? "DUE_TODAY" : "OPEN";
}
var noticeIsFor = (departments, userDepartments) => !departments.length || departments.some((d) => userDepartments.includes(d));
var dailyReportDue = (hotelMinutes2, at) => hotelMinutes2 >= minutesOf4(at);

// src/hr-cycle.ts
var APPLICANT_STAGES = ["APPLIED", "SHORTLISTED", "INTERVIEW", "OFFERED", "HIRED", "REJECTED", "WITHDRAWN", "DECLINED"];
var CLOSED_STAGES = ["HIRED", "REJECTED", "WITHDRAWN", "DECLINED"];
var MOVES = {
  APPLIED: ["SHORTLISTED", "REJECTED", "WITHDRAWN"],
  SHORTLISTED: ["APPLIED", "REJECTED", "WITHDRAWN"],
  INTERVIEW: ["REJECTED", "WITHDRAWN"],
  OFFERED: ["DECLINED", "REJECTED", "WITHDRAWN"],
  HIRED: [],
  REJECTED: [],
  WITHDRAWN: [],
  DECLINED: []
};
var canMoveApplicant = (from, to) => MOVES[from].includes(to);
var INTERVIEWABLE = ["APPLIED", "SHORTLISTED", "INTERVIEW"];
function recruitRoom(p) {
  return Math.max(0, p.approved - (p.holders - p.leaving) - p.recruiting);
}
var INTERVIEW_CRITERIA = [
  { code: "PRESENTATION", label: "Appearance and grooming" },
  { code: "COMMUNICATION", label: "Communication" },
  { code: "SKILLS", label: "Experience and skills for the post" },
  { code: "SERVICE", label: "Attitude to guests and service" },
  { code: "TEAMWORK", label: "Teamwork and attitude to work" },
  { code: "MOTIVATION", label: "Motivation and fit with the hotel" }
];
function interviewScore(scores) {
  const v = INTERVIEW_CRITERIA.map((c) => scores[c.code]);
  if (v.some((x) => x === void 0 || !Number.isInteger(x) || x < 1 || x > 5)) return null;
  return Math.round(v.reduce((a, b) => a + b, 0) / v.length * 10) / 10;
}
var LETTER_KINDS = ["OFFER", "APPOINTMENT", "CONFIRMATION", "QUERY", "WARNING", "FINAL_WARNING", "SERVICE_CERTIFICATE"];
var LETTER_LABEL = {
  OFFER: "Offer of employment",
  APPOINTMENT: "Letter of appointment",
  CONFIRMATION: "Confirmation of appointment",
  QUERY: "Query",
  WARNING: "Warning",
  FINAL_WARNING: "Final warning",
  SERVICE_CERTIFICATE: "Certificate of service"
};
var LETTER_TEMPLATES = {
  OFFER: {
    subject: "Offer of employment: {{position}}",
    body: `Dear {{firstName}},

Following your interview, we are pleased to offer you employment with {{hotel}} as {{position}} in the {{department}} department, on the following terms:

1. Start date: {{startDate}}.
2. Employment: {{employmentType}}.
3. Probation: {{probation}}.
4. Pay: a monthly gross salary of {{gross}} ({{grossWords}}), made up of basic salary and housing, transport, meal and utility allowances, less PAYE, your pension contribution and NHF as the law requires.
5. Hours: {{workWeek}}, on the roster set by your head of department.
6. Annual leave: {{leaveDays}} working days a year, earned through the year.

This offer depends on two satisfactory guarantors, your original certificates, your NIN and, where your post requires it, a medical fitness certificate.

Please sign and return a copy of this letter by {{replyBy}} to accept the offer. We look forward to welcoming you to the team.`
  },
  APPOINTMENT: {
    subject: "Letter of appointment: {{position}}",
    body: `Dear {{firstName}},

We are pleased to confirm your appointment with {{hotel}} as {{position}} in the {{department}} department, with effect from {{startDate}}, on these terms:

1. Staff number: {{staffNo}}.
2. Employment: {{employmentType}}.
3. Probation: {{probation}}.
4. Pay: a monthly gross salary of {{gross}} ({{grossWords}}), less PAYE, your pension contribution and NHF as the law requires. Salaries are paid into your own bank account on the {{payDay}} of each month.
5. Hours: {{workWeek}}, on the roster set by your head of department.
6. Annual leave: {{leaveDays}} working days a year, earned through the year.
7. You will keep to the hotel's rules, its standards of service and its policies, including data protection, as they are given to you.

Please sign and return the copy of this letter to Human Resources to confirm that you accept these terms.`
  },
  CONFIRMATION: {
    subject: "Confirmation of appointment",
    body: `Dear {{firstName}},

Following the review of your probation, we are pleased to confirm your appointment as {{position}} in the {{department}} department with effect from {{confirmedOn}}.

All other terms of your appointment remain as before. Thank you for your work so far; we look forward to your continued contribution.`
  },
  QUERY: {
    subject: "Query: {{title}}",
    body: `Dear {{firstName}},

It has been reported that {{details}}

You are required to explain in writing, within {{replyHours}} hours of receiving this letter and not later than {{replyDue}}, why disciplinary action should not be taken against you. Your reply should be handed to Human Resources.

If no reply is received by then, the hotel will take it that you have no explanation to give and will act on the facts as reported.`
  },
  WARNING: {
    subject: "Warning: {{title}}",
    body: `Dear {{firstName}},

This letter is a formal warning about the following: {{details}}

This conduct falls short of what the hotel expects of its staff. The warning stays on your file until {{validUntil}}. Any further breach in that time may lead to a final warning or other disciplinary action under the hotel's disciplinary procedure.

You may reply in writing to Human Resources if you wish your comments to be kept with this letter.`
  },
  FINAL_WARNING: {
    subject: "Final warning: {{title}}",
    body: `Dear {{firstName}},

This letter is a final warning about the following: {{details}}

You have been warned before about your conduct. This final warning stays on your file until {{validUntil}}. Any further breach in that time may lead to the termination of your employment under the hotel's disciplinary procedure.

You may reply in writing to Human Resources if you wish your comments to be kept with this letter.`
  },
  SERVICE_CERTIFICATE: {
    subject: "Certificate of service",
    body: `To whom it may concern,

This is to certify that {{fullName}} (staff number {{staffNo}}) was employed by {{hotel}} from {{startDate}} to {{leavingDate}}, last as {{position}} in the {{department}} department.

We wish {{firstName}} every success in the future.`
  }
};
function placeholders(template) {
  return [...new Set([...template.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1]))];
}
function fillLetter(template, values) {
  return template.replace(/\{\{(\w+)\}\}/g, (all, k) => {
    const v = values[k];
    return v === null || v === void 0 || v === "" ? all : String(v);
  });
}
var ONES = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
var TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
function below1000(n) {
  const h = Math.floor(n / 100);
  const r = n % 100;
  const rest = r < 20 ? ONES[r] : `${TENS[Math.floor(r / 10)]}${r % 10 ? `-${ONES[r % 10]}` : ""}`;
  return [h ? `${ONES[h]} hundred` : "", h && r ? "and" : "", r ? rest : ""].filter(Boolean).join(" ");
}
function inWords(n) {
  if (n === 0) return "zero";
  const scales = [[1e9, "billion"], [1e6, "million"], [1e3, "thousand"]];
  const parts = [];
  let left = n;
  for (const [size, name] of scales) {
    if (left >= size) {
      parts.push(`${inWords(Math.floor(left / size))} ${name}`);
      left %= size;
    }
  }
  if (left) parts.push(`${parts.length && left < 100 ? "and " : ""}${below1000(left)}`);
  return parts.join(" ");
}
function nairaInWords(amount) {
  const v = dec(amount).toDecimalPlaces(2);
  const naira = Math.floor(v.toNumber());
  const kobo = Math.round(v.minus(naira).times(100).toNumber());
  const s = `${inWords(naira)} naira${kobo ? ` and ${inWords(kobo)} kobo` : ""} only`;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function monthFigures(rows, o) {
  const now = (o.now ?? /* @__PURE__ */ new Date()).getTime();
  const holidays = new Set(o.holidays);
  const onLeave = new Set(o.leaveDates);
  const over = rows.filter((r) => r.endsAt.getTime() <= now);
  const absent = /* @__PURE__ */ new Set();
  const f = { shifts: over.length, worked: 0, late: 0, absentDays: 0, overtimeMinutes: 0, holidayOvertimeMinutes: 0, forgottenClockOuts: 0 };
  for (const r of over) {
    if (!r.clockIn) {
      if (!onLeave.has(r.date)) absent.add(r.date);
      continue;
    }
    f.worked++;
    if ((r.clockIn.getTime() - r.startsAt.getTime()) / 6e4 > o.lateGraceMinutes) f.late++;
    if (r.closedBySystem) {
      f.forgottenClockOuts++;
      continue;
    }
    if (!r.clockOut) continue;
    const extra = Math.floor((r.clockOut.getTime() - r.endsAt.getTime()) / 6e4);
    if (extra > o.afterMinutes) {
      if (holidays.has(r.date)) f.holidayOvertimeMinutes += extra;
      else f.overtimeMinutes += extra;
    }
  }
  for (const r of over) if (r.clockIn) absent.delete(r.date);
  f.absentDays = absent.size;
  return f;
}
function hourlyBasic(basic, workWeekDays, hoursPerDay) {
  return dec(basic).times(12).dividedBy(52 * workWeekDays * hoursPerDay);
}
function overtimePay(basic, minutes3, multiplier, workWeekDays, hoursPerDay) {
  return m2(hourlyBasic(basic, workWeekDays, hoursPerDay).times(minutes3).dividedBy(60).times(multiplier));
}
function hoursAndMinutes(minutes3) {
  const h = Math.floor(minutes3 / 60);
  const m = minutes3 % 60;
  return h && m ? `${h} h ${m} min` : h ? `${h} h` : `${m} min`;
}
function leaveEarnedToDate(fullDays, hireDate, lastDay, leaveYear, yearStartMonth = 1) {
  const mm = String(yearStartMonth).padStart(2, "0");
  const yearStart = `${leaveYear}-${mm}-01`;
  const nextYearStart = `${leaveYear + 1}-${mm}-01`;
  const daysInYear = diffDays(yearStart, nextYearStart);
  const from = hireDate > yearStart ? hireDate : yearStart;
  const employed = lastDay < nextYearStart ? diffDays(from, lastDay) + 1 : diffDays(from, nextYearStart);
  if (employed <= 0) return 0;
  return Math.round(fullDays * employed / daysInYear * 2) / 2;
}
function leaveOwed(earned, taken, adjustments = 0) {
  return Math.round((earned + adjustments - taken) * 10) / 10;
}
function dailyPay(monthlyGross, workWeekDays) {
  return dec(monthlyGross).times(12).dividedBy(52 * workWeekDays);
}
function leavePay(owedDays, daily, deductOvertaken) {
  if (owedDays > 0 || owedDays < 0 && deductOvertaken) return m2(dec(daily).times(owedDays));
  return "0.00";
}

// src/housekeeping.ts
var LOST_FOUND_CATEGORIES = ["MONEY", "JEWELLERY", "ELECTRONICS", "DOCUMENTS", "CLOTHING", "BAGS", "MEDICINE", "PERISHABLE", "OTHER"];
var LOST_FOUND_CATEGORY_NAMES = {
  MONEY: "Money",
  JEWELLERY: "Jewellery & watches",
  ELECTRONICS: "Phones, laptops & electronics",
  DOCUMENTS: "Passports, IDs & bank cards",
  CLOTHING: "Clothing & shoes",
  BAGS: "Bags & luggage",
  MEDICINE: "Medicine",
  PERISHABLE: "Food, drinks & opened toiletries",
  OTHER: "Other"
};
var LOST_FOUND_VALUABLE = ["MONEY", "JEWELLERY", "DOCUMENTS"];
var LOST_FOUND_USUALLY_VALUABLE = [...LOST_FOUND_VALUABLE, "ELECTRONICS"];
var DEFAULT_LOST_FOUND = { perishableDays: 1, medicineDays: 30, documentDays: 30, valuableDays: 180, otherDays: 90 };
function isValuableCategory(category) {
  return LOST_FOUND_VALUABLE.includes(category);
}
function lostFoundKeepDays(category, valuable, rules = DEFAULT_LOST_FOUND) {
  if (category === "PERISHABLE") return rules.perishableDays;
  if (category === "MEDICINE") return rules.medicineDays;
  if (category === "DOCUMENTS") return rules.documentDays;
  return valuable || isValuableCategory(category) ? rules.valuableDays : rules.otherDays;
}
var LOST_FOUND_DISPOSALS = ["DONATED", "DESTROYED", "HANDED_OVER"];
function lostFoundDisposals(category) {
  if (category === "PERISHABLE" || category === "MEDICINE") return ["DESTROYED"];
  if (category === "DOCUMENTS") return ["HANDED_OVER", "DESTROYED"];
  if (category === "MONEY") return ["HANDED_OVER", "DONATED"];
  return ["DONATED", "DESTROYED", "HANDED_OVER"];
}
function deepCleanDue(lastDone, today, rule, slot) {
  const dueOn = lastDone ? addDays(lastDone, rule.everyDays) : addDays(rule.startsOn, Math.floor(slot.position * rule.everyDays / Math.max(1, slot.of)));
  return { dueOn, due: dueOn <= today, daysOverdue: Math.max(0, diffDays(dueOn, today)) };
}
function deepCleanFullRound(doneBefore) {
  return doneBefore % 2 === 0;
}
var hhmm3 = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
function turndownDue(businessDate, at, from, until = "23:59") {
  const now = hotelMinutes(at);
  return hotelDate(at) === businessDate && now >= hhmm3(from) && now < hhmm3(until);
}
var AMENITY_KINDS = ["REFILL", "TOILETRY", "EQUIPMENT"];
var AMENITY_KIND_NAMES = {
  REFILL: "Complimentary refills",
  TOILETRY: "Refillable toiletries",
  EQUIPMENT: "Room equipment"
};
function amenityDefault(a, taskType) {
  if (a.kind === "EQUIPMENT") return 0;
  const full = taskType === "DEPARTURE" || taskType === "DEEP_CLEAN";
  if (a.kind === "TOILETRY") return full ? 1 : 0;
  if (full) return a.par;
  return taskType === "STAYOVER" && a.daily ? a.par : 0;
}
var AMENITY_TASKS = ["DEPARTURE", "STAYOVER", "DEEP_CLEAN", "TURNDOWN"];

// src/stock.ts
var STOCK_CATEGORY_GROUPS = ["Sold to guests", "Rooms and housekeeping", "Food and beverage service", "Engineering and grounds", "Offices and other departments"];
var STOCK_CATEGORY_DEFS = [
  { code: "FOOD", label: "Food", prefix: "FOOD", store: "KITCHEN", group: "Sold to guests", kinds: ["MEAT", "POULTRY", "FISH", "VEG_FRUIT", "DAIRY_EGGS", "DRY_GOODS", "BAKERY", "SPICES_OILS", "FROZEN_FOODS", "CANNED_FOODS"] },
  { code: "BEVERAGE", label: "Beverages (non-alcoholic)", prefix: "BEV", store: "MAIN", group: "Sold to guests", kinds: ["WATER", "SOFT_DRINKS", "ENERGY_DRINKS", "JUICES", "HOT_DRINKS", "SYRUPS"] },
  { code: "MINIBAR", label: "Minibar", prefix: "MINI", store: "MAIN", group: "Sold to guests", kinds: ["MINIBAR", "MINIBAR_SNACKS"] },
  { code: "GUEST_SUPPLIES", label: "Guest amenities", prefix: "AMEN", store: "HK", group: "Rooms and housekeeping", kinds: ["AMENITIES", "DENTAL_SHAVING", "GUEST_DISPOSABLES", "TEA_TRAY", "WELCOME"] },
  { code: "ROOM_SUPPLIES", label: "Room supplies", prefix: "ROOM", store: "HK", group: "Rooms and housekeeping", kinds: ["ROOM_SUPPLIES", "ROOM_COLLATERAL", "KEY_CARDS", "PRAYER", "SMALL_APPLIANCES"] },
  { code: "CLEANING", label: "Cleaning supplies", prefix: "CLEAN", store: "HK", group: "Rooms and housekeeping", kinds: ["CLEANING", "CLEANING_TOOLS", "PAPER_TISSUE", "BIN_LINERS", "KITCHEN_HYGIENE", "PEST_CONTROL"] },
  { code: "LAUNDRY", label: "Laundry supplies", prefix: "LDRY", store: "LAUNDRY", group: "Rooms and housekeeping", kinds: ["LAUNDRY_CHEMICALS", "LAUNDRY_PACKAGING"] },
  { code: "LINEN", label: "Linen", prefix: "LIN", store: "LINEN", group: "Rooms and housekeeping", kinds: ["LINEN", "TOWELS", "TABLE_LINEN", "BATHROBES", "SOFT_FURNISHING"] },
  { code: "UNIFORMS", label: "Uniforms", prefix: "UNIF", store: "LINEN", group: "Rooms and housekeeping", kinds: ["UNIFORMS", "UNIFORM_ACCESSORIES"] },
  { code: "TABLEWARE", label: "Tableware & kitchen utensils", prefix: "TBL", store: "KITCHEN", group: "Food and beverage service", kinds: ["CHINA", "GLASSWARE", "CUTLERY", "KITCHEN_UTENSILS", "SERVING"] },
  { code: "DISPOSABLES", label: "Disposables & packaging", prefix: "DISP", store: "KITCHEN", group: "Food and beverage service", kinds: ["TAKEAWAY", "NAPKINS", "STRAWS_CUPS", "FOIL_FILM", "CHAFING_FUEL"] },
  { code: "DECORATIONS", label: "Decorations & flowers", prefix: "DECO", store: "MAIN", group: "Food and beverage service", kinds: ["FLOWERS", "EVENT_DECOR", "FESTIVE"] },
  { code: "KITCHEN_FUEL", label: "Kitchen fuel", prefix: "KFUEL", store: "KITCHEN", group: "Food and beverage service", kinds: ["COOKING_GAS", "CHARCOAL"] },
  { code: "ENGINEERING", label: "Electrical & plumbing", prefix: "ELEC", store: "ENG", group: "Engineering and grounds", kinds: ["ELECTRICAL", "LIGHTING", "BATTERIES", "PLUMBING", "SANITARY", "SPARES"] },
  { code: "BUILDING", label: "Building materials", prefix: "BLDG", store: "ENG", group: "Engineering and grounds", kinds: ["BUILDING", "PAINT", "HARDWARE"] },
  { code: "HVAC", label: "Air conditioning", prefix: "HVAC", store: "ENG", group: "Engineering and grounds", kinds: ["HVAC", "REFRIGERANT", "HVAC_FILTERS"] },
  { code: "GENERATOR", label: "Generator parts", prefix: "GEN", store: "ENG", group: "Engineering and grounds", kinds: ["GEN_OIL", "GEN_FILTERS", "GEN_PARTS"] },
  { code: "POOL_GARDEN", label: "Pool & garden", prefix: "POOL", store: "ENG", group: "Engineering and grounds", kinds: ["POOL_CHEMICALS", "POOL_EQUIPMENT", "GARDEN"] },
  { code: "FUEL", label: "Fuel (diesel)", prefix: "FUEL", store: "DIESEL", group: "Engineering and grounds", kinds: ["DIESEL"] },
  { code: "VEHICLE", label: "Vehicle supplies", prefix: "VEH", store: "ENG", group: "Engineering and grounds", kinds: ["VEHICLE_OIL", "TYRES", "VEHICLE_PARTS", "CAR_CARE"] },
  { code: "SAFETY", label: "Safety & security", prefix: "SAFE", store: "MAIN", group: "Engineering and grounds", kinds: ["FIRE_SAFETY", "PPE", "SECURITY_SUPPLIES"] },
  { code: "STATIONERY", label: "Printing & stationery", prefix: "STAT", store: "MAIN", group: "Offices and other departments", kinds: ["STATIONERY", "PRINTING", "TILL_ROLLS", "CONFERENCE"] },
  { code: "IT_SUPPLIES", label: "IT supplies", prefix: "IT", store: "MAIN", group: "Offices and other departments", kinds: ["TONER_INK", "IT_ACCESSORIES", "IT_STORAGE", "IT_PARTS"] },
  { code: "MEDICAL", label: "Medical & first aid", prefix: "MED", store: "MAIN", group: "Offices and other departments", kinds: ["FIRST_AID", "MEDICINES", "SANITARY_HYGIENE"] },
  { code: "MARKETING", label: "Marketing materials", prefix: "MKT", store: "MAIN", group: "Offices and other departments", kinds: ["BROCHURES", "BANNERS", "GIVEAWAYS"] },
  { code: "OTHER", label: "Other (to be moved to its category)", prefix: "OTH", store: "MAIN", group: "Offices and other departments", kinds: ["OTHER"] }
];
var STOCK_CATEGORIES = [
  "FOOD",
  "BEVERAGE",
  "MINIBAR",
  "GUEST_SUPPLIES",
  "ROOM_SUPPLIES",
  "CLEANING",
  "LAUNDRY",
  "LINEN",
  "UNIFORMS",
  "TABLEWARE",
  "DISPOSABLES",
  "DECORATIONS",
  "KITCHEN_FUEL",
  "ENGINEERING",
  "BUILDING",
  "HVAC",
  "GENERATOR",
  "POOL_GARDEN",
  "FUEL",
  "VEHICLE",
  "SAFETY",
  "STATIONERY",
  "IT_SUPPLIES",
  "MEDICAL",
  "MARKETING",
  "OTHER"
];
var NEW_ITEM_CATEGORIES = STOCK_CATEGORIES.filter((c) => c !== "OTHER");
var DEF = Object.fromEntries(STOCK_CATEGORY_DEFS.map((d) => [d.code, d]));
var CATEGORY_LABEL = Object.fromEntries(STOCK_CATEGORY_DEFS.map((d) => [d.code, d.label]));
var CATEGORY_PREFIX = Object.fromEntries(STOCK_CATEGORY_DEFS.map((d) => [d.code, d.prefix]));
var CATEGORY_STORE = Object.fromEntries(STOCK_CATEGORY_DEFS.map((d) => [d.code, d.store]));
var STOCK_KINDS = Object.fromEntries(STOCK_CATEGORY_DEFS.map((d) => [d.code, d.kinds]));
var ALL_STOCK_KINDS = STOCK_CATEGORY_DEFS.flatMap((d) => d.kinds);
var STOCK_KIND_LABEL = {
  MEAT: "Meat",
  POULTRY: "Poultry",
  FISH: "Fish & seafood",
  VEG_FRUIT: "Vegetables & fruit",
  DAIRY_EGGS: "Dairy & eggs",
  DRY_GOODS: "Dry goods (rice, flour, pasta, beans)",
  BAKERY: "Bakery",
  SPICES_OILS: "Spices, oils & condiments",
  FROZEN_FOODS: "Frozen foods",
  CANNED_FOODS: "Canned & preserved foods",
  WATER: "Water",
  SOFT_DRINKS: "Soft drinks",
  ENERGY_DRINKS: "Energy & malt drinks",
  JUICES: "Juices",
  HOT_DRINKS: "Tea, coffee & cocoa",
  SYRUPS: "Syrups & mixers",
  MINIBAR: "Minibar drinks",
  MINIBAR_SNACKS: "Minibar snacks",
  AMENITIES: "Bath amenities (soap, shampoo, lotion)",
  DENTAL_SHAVING: "Dental & shaving kits",
  GUEST_DISPOSABLES: "Slippers, shower caps & sewing kits",
  TEA_TRAY: "In-room tea tray (tea, coffee, sugar, creamer)",
  WELCOME: "Welcome & VIP amenities",
  ROOM_SUPPLIES: "Room accessories (hangers, bins, trays)",
  ROOM_COLLATERAL: "Jotters, pens & room folders",
  KEY_CARDS: "Key cards",
  PRAYER: "Prayer room (mats, attar, Qur'an copies, kettles)",
  SMALL_APPLIANCES: "Small appliances (kettles, hair dryers)",
  CLEANING: "Cleaning chemicals",
  CLEANING_TOOLS: "Cleaning tools (brooms, mops, buckets)",
  PAPER_TISSUE: "Toilet & tissue paper",
  BIN_LINERS: "Bin liners & waste bags",
  KITCHEN_HYGIENE: "Kitchen hygiene & dishwashing",
  PEST_CONTROL: "Pest control",
  LAUNDRY_CHEMICALS: "Laundry detergents & chemicals",
  LAUNDRY_PACKAGING: "Laundry bags, tags & hangers",
  LINEN: "Bed linen",
  TOWELS: "Towels (bath, hand, pool)",
  TABLE_LINEN: "Table linen & skirting",
  BATHROBES: "Bathrobes",
  SOFT_FURNISHING: "Curtains & cushion covers",
  UNIFORMS: "Uniforms",
  UNIFORM_ACCESSORIES: "Work shoes, name badges & accessories",
  CHINA: "China & crockery",
  GLASSWARE: "Glassware",
  CUTLERY: "Cutlery",
  KITCHEN_UTENSILS: "Kitchen utensils & small equipment",
  SERVING: "Serving equipment (trays, chafing dishes)",
  TAKEAWAY: "Takeaway packs & bags",
  NAPKINS: "Paper napkins",
  STRAWS_CUPS: "Straws, cups & toothpicks",
  FOIL_FILM: "Foil, cling film & baking paper",
  CHAFING_FUEL: "Chafing fuel",
  FLOWERS: "Flowers",
  EVENT_DECOR: "Event decoration",
  FESTIVE: "Festive decoration",
  COOKING_GAS: "Cooking gas",
  CHARCOAL: "Charcoal",
  ELECTRICAL: "Electrical (cables, sockets, breakers)",
  LIGHTING: "Lighting & bulbs",
  BATTERIES: "Batteries",
  PLUMBING: "Plumbing",
  SANITARY: "Sanitary fittings",
  SPARES: "Spare parts & tools",
  BUILDING: "Building materials (cement, tiles, wood)",
  PAINT: "Paint & finishes",
  HARDWARE: "Hardware (locks, hinges, screws)",
  HVAC: "Air-conditioning parts",
  REFRIGERANT: "Refrigerant gas",
  HVAC_FILTERS: "Air-conditioning filters",
  GEN_OIL: "Generator oil & lubricants",
  GEN_FILTERS: "Generator filters",
  GEN_PARTS: "Generator parts",
  POOL_CHEMICALS: "Pool chemicals",
  POOL_EQUIPMENT: "Pool equipment & test kits",
  GARDEN: "Garden (fertiliser, seeds, tools)",
  DIESEL: "Diesel",
  VEHICLE_OIL: "Engine oil & lubricants",
  TYRES: "Tyres & batteries",
  VEHICLE_PARTS: "Vehicle parts",
  CAR_CARE: "Car wash & care",
  FIRE_SAFETY: "Fire safety (extinguisher refills, detectors)",
  PPE: "Protective gear",
  SECURITY_SUPPLIES: "Security supplies (torches, padlocks, seals)",
  STATIONERY: "Stationery",
  PRINTING: "Printed forms & folios",
  TILL_ROLLS: "Till & printer rolls",
  CONFERENCE: "Conference supplies (notepads, markers, flip charts)",
  TONER_INK: "Toner & ink",
  IT_ACCESSORIES: "Cables, mice & keyboards",
  IT_STORAGE: "Flash drives & storage",
  IT_PARTS: "Small IT parts",
  FIRST_AID: "First-aid kits & dressings",
  MEDICINES: "Medicines",
  SANITARY_HYGIENE: "Sanitary & hygiene supplies",
  BROCHURES: "Brochures & flyers",
  BANNERS: "Banners & signage",
  GIVEAWAYS: "Branded giveaways",
  OTHER: "Other"
};
var kindFits = (category, kind) => !kind || (STOCK_KINDS[category] ?? []).includes(kind);
var categoryDef = (category) => DEF[category];
var itemCode = (category, n) => `${CATEGORY_PREFIX[category] ?? "ITM"}-${String(n).padStart(4, "0")}`;
var STOCK_UNITS = [
  "piece",
  "bottle",
  "can",
  "sachet",
  "packet",
  "pack",
  "box",
  "carton",
  "tin",
  "jar",
  "tube",
  "roll",
  "ream",
  "pair",
  "set",
  "bag",
  "sack",
  "keg",
  "jerrycan",
  "drum",
  "cylinder",
  "tray",
  "bunch",
  "dozen",
  "kg",
  "gram",
  "litre",
  "metre"
];
var PACK_UNITS = ["crate", "carton", "case", "pack", "box", "bag", "sack", "keg", "jerrycan", "drum", "tin", "bundle", "bale", "roll", "ream", "dozen", "tray", "set", "cylinder", "pallet"];
var SAME_PLURAL = /* @__PURE__ */ new Set(["kg", "dozen"]);
function unitWord(unit, n) {
  if (Number(n) === 1 || SAME_PLURAL.has(unit)) return unit;
  return /(s|x|ch|sh)$/.test(unit) ? `${unit}es` : `${unit}s`;
}
function itemPack(item) {
  const size = Number(item.unitsPerPurchase ?? 1);
  return item.purchaseUnit && size > 1 ? { unit: item.purchaseUnit, size } : null;
}
function inPacks(qty, size) {
  const q = dec(qty);
  const s = dec(size);
  if (!s.greaterThan(0)) return { packs: 0, loose: q.toNumber() };
  const packs = q.dividedBy(s).floor();
  return { packs: packs.toNumber(), loose: q.minus(packs.times(s)).toDecimalPlaces(3).toNumber() };
}
function packText(qty, unit, packUnit, packSize) {
  const units = `${dec(qty).toDecimalPlaces(3).toNumber()} ${unitWord(unit, dec(qty).toNumber())}`;
  if (!packUnit || !packSize || !dec(packSize).greaterThan(1)) return units;
  const { packs, loose } = inPacks(qty, packSize);
  if (!packs) return units;
  return loose ? `${packs} ${unitWord(packUnit, packs)} and ${loose} ${unitWord(unit, loose)}` : `${packs} ${unitWord(packUnit, packs)} (${units})`;
}
var unitPriceOfPack = (packPrice, size) => dec(packPrice).dividedBy(size).toDecimalPlaces(4);
function lineValue(qty, unitPrice, pack) {
  const q = dec(qty);
  if (pack?.size != null && pack.price != null && dec(pack.size).greaterThan(0)) {
    const packs = q.dividedBy(pack.size);
    if (packs.isInteger()) return round2(packs.times(pack.price));
  }
  return round2(q.times(unitPrice));
}

// src/stores.ts
var PERISHABLE = ["MEAT", "POULTRY", "FISH", "VEG_FRUIT", "DAIRY_EGGS", "BAKERY", "FLOWERS"];
var DRY = [
  "DRY_GOODS",
  "SPICES_OILS",
  "FROZEN_FOODS",
  "CANNED_FOODS",
  "WATER",
  "SOFT_DRINKS",
  "ENERGY_DRINKS",
  "JUICES",
  "HOT_DRINKS",
  "SYRUPS",
  "MINIBAR",
  "MINIBAR_SNACKS",
  "AMENITIES",
  "TEA_TRAY",
  "MEDICINES",
  "FIRST_AID"
];
var CHEMICAL = ["CLEANING", "KITCHEN_HYGIENE", "PEST_CONTROL", "LAUNDRY_CHEMICALS", "POOL_CHEMICALS"];
var COLD_CHAIN = { MEAT: "CHILLED", DAIRY_EGGS: "CHILLED", POULTRY: "FROZEN", FISH: "FROZEN", FROZEN_FOODS: "FROZEN" };
var HALAL_KINDS = ["MEAT", "POULTRY"];
function expiryClass(category, kind) {
  if (kind && PERISHABLE.includes(kind)) return "PERISHABLE";
  if (kind && CHEMICAL.includes(kind)) return "CHEMICAL";
  if (kind && DRY.includes(kind)) return "DRY";
  if (!kind && ["FOOD", "BEVERAGE", "MINIBAR", "GUEST_SUPPLIES"].includes(category)) return "DRY";
  if (!kind && (category === "CLEANING" || category === "LAUNDRY")) return "CHEMICAL";
  return null;
}
function temperatureVerdict(t, limits) {
  if (t > limits.refuseAbove) return "REFUSE";
  return t > limits.max ? "WARM" : "OK";
}
function receivingChecks(category, kind) {
  return {
    expiry: expiryClass(category, kind),
    coldChain: kind && COLD_CHAIN[kind] || null,
    halal: !!kind && HALAL_KINDS.includes(kind)
  };
}
var toMin = (hhmm4) => {
  const [h, m] = hhmm4.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
function inWindows(windows, minutes3) {
  return windows.some((w) => {
    const [a, b] = w.split("-");
    return !!a && !!b && minutes3 >= toMin(a) && minutes3 <= toMin(b);
  });
}
function hotelClock(at = /* @__PURE__ */ new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Africa/Lagos", hour: "2-digit", minute: "2-digit", weekday: "short", hourCycle: "h23" }).formatToParts(at);
  const get = (t) => parts.find((p) => p.type === t)?.value ?? "";
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
  return { minutes: Number(get("hour")) * 60 + Number(get("minute")), weekday };
}
function receivedOutsideHours(s, at, perishable) {
  if (!s.receivingDays.includes(at.weekday)) return true;
  if (!inWindows([s.receivingHours], at.minutes)) return true;
  return perishable && at.minutes > toMin(s.perishablesBy);
}
function countNeedsExplanation(systemQty, countedQty, unitCost, limit) {
  const diff = countedQty - systemQty;
  if (diff === 0) return false;
  if (Math.abs(diff * unitCost) > limit.amount) return true;
  return systemQty === 0 ? true : Math.abs(diff) / systemQty * 100 > limit.percent;
}
function invoiceTolerance(received, t) {
  return Math.max(t.amount, Math.round(received * t.percent) / 100);
}
var RECEIVING_KINDS = [...PERISHABLE, ...DRY, ...CHEMICAL].filter((k) => Object.values(STOCK_KINDS).some((ks) => ks.includes(k)));
export {
  ACTIVITY_KINDS,
  ACTIVITY_KIND_LABEL,
  ALERT_MOMENTS,
  ALERT_MOMENT_LABEL,
  ALLERGENS,
  ALL_PERMISSIONS,
  ALL_STOCK_KINDS,
  AMENITY_KINDS,
  AMENITY_KIND_NAMES,
  AMENITY_TASKS,
  APPLICANT_STAGES,
  BILLING_OPTIONS,
  BREACH_NOTIFY_HOURS,
  CAMPAIGN_FIELDS,
  CATEGORY_LABEL,
  CATEGORY_PREFIX,
  CATEGORY_STORE,
  CHANNELS,
  CLOSED_STAGES,
  COLD_CHAIN,
  COMPANY_TYPES,
  CURRENCY_SYMBOL,
  D,
  DEFAULT_LOST_FOUND,
  DEFAULT_ROLES,
  DERIVE_TYPES,
  DESK_MESSAGE_KINDS,
  DESK_MESSAGE_LABEL,
  DIETARY_ALLERGENS,
  FOLIO_ACCOUNT_TYPES,
  FOLIO_STATUSES,
  FRONT_DESK_TERMINALS,
  GENDERS,
  GL_ACCOUNT_TYPES,
  GUARANTEE_STATUSES,
  HALAL_KINDS,
  HK_STATUSES,
  INTERVIEWABLE,
  INTERVIEW_CRITERIA,
  INVENTORY_STATUSES,
  JOURNAL_SOURCES,
  JOURNAL_STATUSES,
  LEAD_KINDS,
  LEAD_KIND_LABEL,
  LEAD_SOURCES,
  LEAD_SOURCE_LABEL,
  LEAD_STAGES,
  LEAD_STAGE_LABEL,
  LEDGERS,
  LETTER_KINDS,
  LETTER_LABEL,
  LETTER_TEMPLATES,
  LOST_FOUND_CATEGORIES,
  LOST_FOUND_CATEGORY_NAMES,
  LOST_FOUND_DISPOSALS,
  LOST_FOUND_USUALLY_VALUABLE,
  LOST_FOUND_VALUABLE,
  LOST_REASONS,
  LOST_REASON_LABEL,
  MANAGEMENT_DEPARTMENT,
  MEAL_PLANS,
  MFA_ROLES,
  MONEY_PERMISSIONS,
  NEW_ITEM_CATEGORIES,
  NIGERIA_PAYROLL_2026,
  NIGHT_AUDIT_STATUSES,
  NOT_EMPLOYEE_ROLES,
  OCCUPANCY_STATUSES,
  ONLINE_GATEWAYS,
  OPEN_STAGES,
  PACKAGE_UNITS,
  PACK_UNITS,
  PAYMENT_METHODS,
  PAY_COMPONENTS,
  PAY_COMPONENT_LABEL,
  PERIOD_STATUSES,
  PERMISSIONS,
  PRAYERS,
  PROMO_CODE,
  PROTECTED_ROLES,
  RATING_LABELS,
  RECEIVING_DEPARTMENTS,
  RECEIVING_KINDS,
  RESERVATION_STATUSES,
  REVIEW_CRITERIA,
  ROOM_BLOCK_TYPES,
  ROOM_CATEGORIES,
  SAFE_BOX_CODE,
  SCREEN_SCOPE,
  SHIFT_FREE_ROLES,
  STAGE_PROBABILITY,
  STOCK_CATEGORIES,
  STOCK_CATEGORY_DEFS,
  STOCK_CATEGORY_GROUPS,
  STOCK_KINDS,
  STOCK_KIND_LABEL,
  STOCK_UNITS,
  TAXABLE_CATEGORIES,
  TURNAWAY_REASONS,
  TURNAWAY_REASON_LABEL,
  TXN_CATEGORIES,
  TXN_KINDS,
  VERIFICATION_STATUSES,
  actionStanding,
  addDays,
  addressSchema,
  allergyClashes,
  allowanceSchema,
  amenityDefault,
  annualAllowance,
  assignRoomSchema,
  auditAllowed,
  auditLate,
  availability,
  blacklistSchema,
  breachClock,
  budgetVariance,
  canMoveApplicant,
  canMoveStage,
  cancelReservationSchema,
  categoryDef,
  changePasswordSchema,
  checkInSchema,
  checkOutSchema,
  code,
  companyInputSchema,
  computePay,
  computeTaxes,
  contractStanding,
  costPercent,
  countNeedsExplanation,
  createReservationSchema,
  dailyPay,
  dailyReportDue,
  dayOfWeek,
  dayOffAllowance,
  dayOffWindow,
  dec,
  deepCleanDue,
  deepCleanFullRound,
  deviceRosterSchema,
  diffDays,
  eachDay,
  earlyCheckInFee,
  expiryClass,
  fillLetter,
  formatDate,
  formatMoney,
  fromBase,
  fuelEconomy,
  fuelFlag,
  guestInputSchema,
  guestSearchSchema,
  guestUpdateSchema,
  hijriDate,
  hotelClock,
  hotelDate,
  hotelMinutes,
  hourlyBasic,
  hoursAndMinutes,
  idLast4,
  inPacks,
  inWindows,
  interviewScore,
  invoiceTolerance,
  iqamaTime,
  isIsoDate,
  isValuableCategory,
  isZero,
  isoDate,
  itemCode,
  itemPack,
  jobCost,
  keyCardSettlement,
  keyOverdue,
  kindFits,
  lateCheckOutFee,
  laundryDueAt,
  laundryHours,
  leaveDays,
  leaveEarnedToDate,
  leaveOwed,
  leavePay,
  letGoSchema,
  lineValue,
  linenRequired,
  loginSchema,
  lostFoundDisposals,
  lostFoundKeepDays,
  m2,
  membershipEnds,
  menuEngineering,
  mfaCodeSchema,
  mfaDisableSchema,
  mfaVerifySchema,
  money,
  monthFigures,
  monthlyDepreciation,
  nairaInWords,
  netPromoterScore,
  newWindowSchema,
  nextRenewal,
  noShowDue,
  nonNegativeMoney,
  noticeIsFor,
  openAt,
  optionalText,
  ordinaryLogin,
  overallRating,
  overdueToClose,
  overtimePay,
  packText,
  pagination,
  paperStanding,
  passwordPolicy,
  pastVisitorCutoff,
  patrolRound,
  patrolRounds,
  periodDays,
  personalise,
  pinLoginSchema,
  pinSchema,
  placeholders,
  plateCost,
  poolHoursPaid,
  poolTestResult,
  poolTestsDue,
  positiveMoney,
  postChargeSchema,
  postPaymentSchema,
  prayerTimes,
  pricesIncludeTaxesFor,
  qiblaBearing,
  quoteSchema,
  readingsDue,
  receivedOutsideHours,
  receivingChecks,
  receivingDepartments,
  recruitRoom,
  refundSchema,
  renewalStanding,
  reservationSearchSchema,
  returnDueDate,
  returnStatus,
  reverseSchema,
  roomCheckDue,
  roomMoveSchema,
  round2,
  routingSchema,
  sameDayCancellationPercent,
  sameDayMoveFee,
  screenScope,
  serviceDue,
  setPinSchema,
  shiftFree,
  splitGross,
  sum,
  taskDue,
  temperatureOk,
  temperatureVerdict,
  ticketAge,
  ticketDueAt,
  toBase,
  todayIn,
  transferSchema,
  tripsOverlap,
  turndownDue,
  unitPriceOfPack,
  unitWord,
  unloggedKm,
  updateReservationSchema,
  upgradeTotal,
  uuid,
  verifyTransferSchema,
  weightedPipeline,
  yearlyTax,
  zonedDateTime
};
//# sourceMappingURL=index.mjs.map